import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { EtiquetaConfig } from '../../../services/clientes';
import { criarConferencia, registrarLeitura, transferirLeituraEntrePaletes, buscarDadosPaleteDescarregamento, identificarPaletePorEtiqueta, analisarDivergenciasVolumes } from '../../../services/conferencia';
import type { AnaliseVolumePedido } from '../../../services/conferencia';
import type { LeituraResponse } from '../../../types/conferencia';
import { salvarLeituraPendente, salvarConferenciaPendente, gerarTempConferenciaId } from '../../../services/offlineQueue';
import { buscarDadosPaleteDescarregamentoOffline } from '../../../services/offlineData';
import { utils } from '../../../utils';
import { criarDivergenciaOcorrenciaBatch } from '../../../services/divergenciaOcorrencias';
import type { DivergenciaOcorrenciaCreateData } from '../../../services/divergenciaOcorrencias';
import { listarTiposOcorrencia } from '../../../services/tiposOcorrenciaDivergencia';
import type { TipoOcorrenciaDivergencia } from '../../../services/tiposOcorrenciaDivergencia';
import { buscarConfiguracoesConferencia } from '../../../services/configuracoesConferencia';
import CameraScannerModal from '../../../components/Common/CameraScannerModal';
import { criarNaoConformidade } from '../../../services/naoConformidades';
import { playScanSound, playDuplicateSound } from '../../../utils/soundAlerts';
import '../PreCarregamento/PreCarregamento.css';

type Etapa = 'palete' | 'scanning' | 'divergencias';

type EtiquetaStatus = 'sucesso' | 'conferida' | 'pendente' | 'duplicado' | 'divergencia' | 'erro_parse' | 'erro' | 'removida';

interface HistoricoItem {
  id: number;
  codigo: string;
  status: EtiquetaStatus;
  mensagem?: string;
  timestamp: Date;
  leituraId?: number;
  conferenciaIdOrigem?: number;
}

interface DadosEtiqueta {
  campo: string;
  valor: string;
  posInicio?: number;
  posFim?: number;
}

interface PaleteInfo {
  id: number;
  numero_palete: number;
  area_nome: string;
  area_codigo: string;
  area_regiao: string;
  operacao_id: number;
  numero_operacao: number;
  doca: string | null;
  area_entrega_id: number;
  tipo_palete?: string;
  data_agendamento?: string;
}

interface ClienteInfo {
  id: number;
  razao_social: string;
  cnpj: string;
}

interface DescarregamentoProps {
  backUrl?: string;
}

// Tipos de ocorrência padrão (fallback se a API não retornar nenhum)
const OCORRENCIA_TIPOS_FALLBACK = [
  { value: 'volume_faltante', label: 'Volume faltante' },
  { value: 'outro', label: 'Outro' },
];

interface DivergenciaFormData {
  pedidoKey: string;
  ocorrencia_tipo: string;
  justificativa: string;
  selected: boolean;
  selectedEtiquetas: Set<number>;
  selectedFaltantes: Set<number>;
}

const STATUS_CONFIG: Record<EtiquetaStatus, { icon: string; color: string; bg: string; label: string }> = {
  sucesso: { icon: 'fas fa-check-circle', color: '#27ae60', bg: '#e8f5e9', label: 'Sucesso' },
  conferida: { icon: 'fas fa-check-double', color: '#2196f3', bg: '#e3f2fd', label: 'Conferida' },
  pendente: { icon: 'fas fa-clock', color: '#ff9800', bg: '#fff3e0', label: 'Pendente' },
  duplicado: { icon: 'fas fa-clone', color: '#f39c12', bg: '#fff3e0', label: 'Duplicado' },
  divergencia: { icon: 'fas fa-exclamation-triangle', color: '#e74c3c', bg: '#ffebee', label: 'Divergência' },
  erro_parse: { icon: 'fas fa-times-circle', color: '#9b59b6', bg: '#f3e5f5', label: 'Não Reconhecida' },
  erro: { icon: 'fas fa-times-circle', color: '#e74c3c', bg: '#ffebee', label: 'Erro' },
  removida: { icon: 'fas fa-trash-alt', color: '#6b7280', bg: '#f3f4f6', label: 'Removida' },
};

const DescarregamentoConferencia: React.FC<DescarregamentoProps> = ({ backUrl = '/conferencia' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isPWA = backUrl === '/conferente';

  const [etapa, setEtapa] = useState<Etapa>('palete');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Configurações globais
  const [isAutoDetect, setIsAutoDetect] = useState(false);
  const [exibirAreaDescarregamento, setExibirAreaDescarregamento] = useState(true);
  const [habilitarCameraScanner, setHabilitarCameraScanner] = useState(false);
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [showScanOptions, setShowScanOptions] = useState(false);
  const scanOptionsRef = useRef<HTMLDivElement>(null);

  // Palete
  const [numeroPalete, setNumeroPalete] = useState('');
  const [paleteInfo, setPaleteInfo] = useState<PaleteInfo | null>(null);
  const paleteRef = useRef<HTMLInputElement>(null);

  // Pre-carregamento count for comparison
  const [totalVolumesPreCarreg, setTotalVolumesPreCarreg] = useState(0);

  // Cliente (auto-detected from palete)
  const [cliente, setCliente] = useState<ClienteInfo | null>(null);

  // Etiqueta configs (from palete lookup)
  const [etiquetaModelos, setEtiquetaModelos] = useState<any[]>([]);
  const [etiquetaConfigs, setEtiquetaConfigs] = useState<any[]>([]);

  // Scanning (input não-controlado para performance máxima em bipagem)
  const etiquetaRef = useRef<HTMLInputElement>(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [ultimoStatus, setUltimoStatus] = useState<{ status: EtiquetaStatus; mensagem: string } | null>(null);

  // Conferencia
  const [conferenciaId, setConferenciaId] = useState<number | null>(null);

  // Historico
  const [historico, setHistorico] = useState<HistoricoItem[]>([]);
  const [historicoVisivel, setHistoricoVisivel] = useState(false);
  const nextId = useRef(0);

  // Divergências
  const [analiseVolumes, setAnaliseVolumes] = useState<AnaliseVolumePedido[]>([]);
  const [divergenciaForms, setDivergenciaForms] = useState<Record<string, DivergenciaFormData>>({});
  const [divergenciaLoading, setDivergenciaLoading] = useState(false);
  const [divergenciaSaving, setDivergenciaSaving] = useState(false);
  const [divergenciaSuccess, setDivergenciaSuccess] = useState(false);
  const [pedidosDivergentesRemovidos, setPedidosDivergentesRemovidos] = useState<string[]>([]);
  const [modoOcorrenciaUnica, setModoOcorrenciaUnica] = useState(false);
  const [ocorrenciaUnicaTipo, setOcorrenciaUnicaTipo] = useState('volume_faltante');
  const [ocorrenciaUnicaJustificativa, setOcorrenciaUnicaJustificativa] = useState('');
  const [expandedPedido, setExpandedPedido] = useState<string | null>(null);

  // Totais logísticos (peso, cubagem, volumes)
  const [totaisLogisticos, setTotaisLogisticos] = useState({ peso_total: 0, cubagem_total: 0, volumes_total: 0 });

  // Rastreamento de volumes por pedido em tempo real
  const [pedidosVolumes, setPedidosVolumes] = useState<Record<string, { total_volumes: number; volumes_bipados: Set<number>; volume_excedente: boolean; codigos_bipados: Map<number, string> }>>({});

  // Pedidos com ponto de corte
  const [pedidosComCorte, setPedidosComCorte] = useState<string[]>([]);

  // Modal de duplicata
  const [duplicataModal, setDuplicataModal] = useState<{
    show: boolean;
    codigo: string;
    volumeAtual?: number | null;
    totalVolumes?: number | null;
    numeroPedido?: string | null;
    leituraId?: number;
  }>({ show: false, codigo: '' });
  const [duplicataLoading, setDuplicataLoading] = useState(false);
  const [duplicataOcorrenciaManual, setDuplicataOcorrenciaManual] = useState<string>('');
  const [duplicataObservacao, setDuplicataObservacao] = useState<string>('');

  // Modal de transferência entre paletes
  const [transferenciaModal, setTransferenciaModal] = useState<{
    show: boolean;
    codigo: string;
    numeroPaleteAnterior: number | null;
    conferenciaIdOrigem: number;
    leituraIdOrigem: number;
    dadosDecodificados: Record<string, unknown>;
  }>({ show: false, codigo: '', numeroPaleteAnterior: null, conferenciaIdOrigem: 0, leituraIdOrigem: 0, dadosDecodificados: {} });
  const [transferenciaLoading, setTransferenciaLoading] = useState(false);

  // Tipos de ocorrência completos (incluindo automáticos) para o modal de duplicata
  const [todosOcorrenciaTipos, setTodosOcorrenciaTipos] = useState<TipoOcorrenciaDivergencia[]>([]);
  const temAutomaticoDuplicata = useMemo(() => {
    const tiposAuto = todosOcorrenciaTipos.filter(t => t.automatico && t.tipo_automatico);
    return tiposAuto.some(t => t.tipo_automatico === 'erro_conferente') && tiposAuto.some(t => t.tipo_automatico === 'duplicata_cliente');
  }, [todosOcorrenciaTipos]);

  // Tipos de ocorrência dinâmicos
  const [tiposOcorrencia, setTiposOcorrencia] = useState<{ value: string; label: string }[]>(OCORRENCIA_TIPOS_FALLBACK);

  // Fechar menu de opções ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (scanOptionsRef.current && !scanOptionsRef.current.contains(e.target as Node)) {
        setShowScanOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Carregar tipos de ocorrência e configurações do backend
  useEffect(() => {
    const carregarTipos = async () => {
      try {
        const result = await listarTiposOcorrencia(true, true);
        if (result.success && result.tipos.length > 0) {
          setTiposOcorrencia(result.tipos.map((t: TipoOcorrenciaDivergencia) => ({
            value: t.codigo,
            label: t.nome,
          })));
        }
        const resultTodos = await listarTiposOcorrencia(true, false);
        if (resultTodos.success) {
          setTodosOcorrenciaTipos(resultTodos.tipos);
        }
      } catch {
        // Mantém fallback em caso de erro
      }
    };
    carregarTipos();
  }, []);

  // Carregar configurações globais na inicialização
  useEffect(() => {
    const carregarConfigs = async () => {
      try {
        const result = await buscarConfiguracoesConferencia();
        if (result.success && result.configuracao) {
          if (result.configuracao.modo_descarregamento === 'auto_detectar') {
            setIsAutoDetect(true);
            setEtapa('scanning');
          }
          if (result.configuracao.exibir_area_descarregamento === false) {
            setExibirAreaDescarregamento(false);
          }
          if (result.configuracao.habilitar_camera_scanner === true) {
            setHabilitarCameraScanner(true);
          }
        }
      } catch {
        // Fallback: usar defaults
      }
    };
    carregarConfigs();
  }, []);

  // Focus management
  useEffect(() => {
    if (etapa === 'palete' && paleteRef.current) {
      paleteRef.current.focus();
    }
    if (etapa === 'scanning' && etiquetaRef.current) {
      etiquetaRef.current.focus();
    }
  }, [etapa]);

  // Manter foco no input de bipagem durante etapa scanning
  useEffect(() => {
    if (etapa !== 'scanning') return;
    const handleFocusBack = () => {
      setTimeout(() => {
        if (etapa === 'scanning' && etiquetaRef.current && document.activeElement !== etiquetaRef.current) {
          etiquetaRef.current.focus();
        }
      }, 100);
    };
    document.addEventListener('click', handleFocusBack);
    return () => document.removeEventListener('click', handleFocusBack);
  }, [etapa]);

  // Auto-clear last status after 4 seconds
  useEffect(() => {
    if (!ultimoStatus) return;
    const timer = setTimeout(() => setUltimoStatus(null), 4000);
    return () => clearTimeout(timer);
  }, [ultimoStatus]);

  // Play sound alert when scan status changes
  useEffect(() => {
    if (ultimoStatus?.status) {
      playScanSound(ultimoStatus.status);
    }
  }, [ultimoStatus]);

  // Summary counters
  const contadores = {
    total: historico.filter(h => h.status !== 'removida' && h.status !== 'pendente').length,
    sucesso: historico.filter(h => h.status === 'sucesso' || h.status === 'conferida').length,
    pendente: historico.filter(h => h.status === 'pendente').length,
    duplicado: historico.filter(h => h.status === 'duplicado').length,
    divergencia: historico.filter(h => h.status === 'divergencia').length,
    erro: historico.filter(h => h.status === 'erro_parse' || h.status === 'erro').length,
    removida: historico.filter(h => h.status === 'removida').length,
  };

  // Verificação dinâmica de divergências em tempo real
  const temDivergenciaReal = useMemo(() => {
    for (const [pedidoKey, pedido] of Object.entries(pedidosVolumes)) {
      if (pedido.volume_excedente) return true;
      // Pedidos com ponto de corte: o esperado é apenas o que foi bipado (parcial)
      const isCorte = pedidosComCorte.includes(pedidoKey);
      if (isCorte) continue;
      if (pedido.volumes_bipados.size < pedido.total_volumes) return true;
    }
    return false;
  }, [pedidosVolumes, pedidosComCorte]);

  // Comparação pré-carregamento vs descarregamento
  const diferencaPreCarreg = useMemo(() => {
    if (totalVolumesPreCarreg <= 0) return null;
    const bipados = contadores.sucesso;
    if (bipados === totalVolumesPreCarreg) return { status: 'ok' as const, diferenca: 0 };
    return {
      status: bipados < totalVolumesPreCarreg ? 'faltante' as const : 'excedente' as const,
      diferenca: Math.abs(bipados - totalVolumesPreCarreg),
    };
  }, [totalVolumesPreCarreg, contadores.sucesso]);

  const addHistorico = (item: Omit<HistoricoItem, 'id'>) => {
    setHistorico(prev => [{ ...item, id: nextId.current++ }, ...prev]);
  };

  const formatCnpj = (value: string): string => {
    const nums = value.replace(/\D/g, '');
    if (nums.length <= 2) return nums;
    if (nums.length <= 5) return `${nums.slice(0, 2)}.${nums.slice(2)}`;
    if (nums.length <= 8) return `${nums.slice(0, 2)}.${nums.slice(2, 5)}.${nums.slice(5)}`;
    if (nums.length <= 12) return `${nums.slice(0, 2)}.${nums.slice(2, 5)}.${nums.slice(5, 8)}/${nums.slice(8)}`;
    return `${nums.slice(0, 2)}.${nums.slice(2, 5)}.${nums.slice(5, 8)}/${nums.slice(8, 12)}-${nums.slice(12)}`;
  };

  // Gerar código de etiqueta irmã (substituir volume_atual no código original)
  const gerarCodigoIrmao = (codigoOriginal: string, volumeDesejado: number): string | null => {
    const parsed = parseEtiqueta(codigoOriginal);
    if (!parsed.success) return null;
    const modelos = etiquetaModelos || [];
    const modeloComVolume = modelos.find((m: any) => m.ativo && m.campo_volume_atual);
    if (!modeloComVolume) return null;
    const volumeDado = parsed.dados.find(d => d.campo === modeloComVolume.campo_volume_atual);
    if (!volumeDado || volumeDado.posInicio === undefined || volumeDado.posFim === undefined) return null;
    // Determinar a largura correta para o volume usando total_volumes como referência.
    // O regex pode capturar apenas os dígitos significativos sem zeros à esquerda
    // (ex: regex "0*(\d+)" captura "1" de "0001"), mas o campo total_volumes preserva
    // o formato correto (ex: "0006" = 4 dígitos). Usamos essa largura como referência.
    let larguraVolume = volumeDado.posFim - volumeDado.posInicio;
    let posInicioAjustado = volumeDado.posInicio;
    if (modeloComVolume.campo_total_volumes) {
      const totalVolumesDado = parsed.dados.find(d => d.campo === modeloComVolume.campo_total_volumes);
      if (totalVolumesDado && totalVolumesDado.posInicio !== undefined && totalVolumesDado.posFim !== undefined) {
        const larguraTotal = totalVolumesDado.posFim - totalVolumesDado.posInicio;
        if (larguraTotal > larguraVolume) {
          const diferenca = larguraTotal - larguraVolume;
          const prefixo = codigoOriginal.substring(volumeDado.posInicio - diferenca, volumeDado.posInicio);
          if (/^0+$/.test(prefixo)) {
            posInicioAjustado = volumeDado.posInicio - diferenca;
            larguraVolume = larguraTotal;
          }
        }
      }
    }
    const volumeFormatado = String(volumeDesejado).padStart(larguraVolume, '0');
    return codigoOriginal.substring(0, posInicioAjustado)
      + volumeFormatado
      + codigoOriginal.substring(volumeDado.posFim);
  };

  // Parse etiqueta using client configs
  const parseEtiqueta = (codigo: string, overrideModelos?: any[], overrideConfigs?: any[]): { success: boolean; dados: DadosEtiqueta[]; erro?: string } => {
    const modelos = overrideModelos || etiquetaModelos || [];
    let dados: DadosEtiqueta[] = [];

    if (modelos.length > 0) {
      for (const modelo of modelos) {
        if (!modelo.ativo) continue;
        const configs = (modelo.configs || []).filter((c: EtiquetaConfig) => c.ativo);
        if (configs.length === 0) continue;

        const dadosTentativa: DadosEtiqueta[] = [];
        let obrigatorioFaltando = false;

        for (const config of configs) {
          try {
            if (config.regex_pattern.startsWith('VALOR_PADRAO=')) {
              dadosTentativa.push({ campo: config.nome_campo, valor: config.regex_pattern.substring(13) });
              continue;
            }
            const regex = new RegExp(config.regex_pattern, 'd');
            const match = regex.exec(codigo);
            if (match) {
              const valor = match[1] || match[0];
              let posInicio: number | undefined;
              let posFim: number | undefined;
              if (match[1] !== undefined && (match as any).indices?.[1]) {
                // Usar indices do regex (flag 'd') para posição exata do grupo de captura
                [posInicio, posFim] = (match as any).indices[1];
              } else if (match[1] !== undefined) {
                // Fallback sem indices
                posInicio = match.index;
                posFim = match.index + match[0].length;
              } else {
                posInicio = match.index;
                posFim = match.index + match[0].length;
              }
              dadosTentativa.push({ campo: config.nome_campo, valor, posInicio, posFim });
            } else if (config.obrigatorio) {
              obrigatorioFaltando = true;
              break;
            }
          } catch {
            // Regex invalido, ignorar
          }
        }

        if (!obrigatorioFaltando && dadosTentativa.length > 0) {
          dados = dadosTentativa;
          break;
        }
      }
    } else {
      const configs = (overrideConfigs || etiquetaConfigs || []).filter((c: EtiquetaConfig) => c.ativo);
      for (const config of configs) {
        try {
          if (config.regex_pattern.startsWith('VALOR_PADRAO=')) {
            dados.push({ campo: config.nome_campo, valor: config.regex_pattern.substring(13) });
            continue;
          }
          const regex = new RegExp(config.regex_pattern, 'd');
          const match = regex.exec(codigo);
          if (match) {
            const valor = match[1] || match[0];
            let posInicio: number | undefined;
            let posFim: number | undefined;
            if (match[1] !== undefined && (match as any).indices?.[1]) {
              // Usar indices do regex (flag 'd') para posição exata do grupo de captura
              [posInicio, posFim] = (match as any).indices[1];
            } else if (match[1] !== undefined) {
              // Fallback sem indices
              posInicio = match.index;
              posFim = match.index + match[0].length;
            } else {
              posInicio = match.index;
              posFim = match.index + match[0].length;
            }
            dados.push({ campo: config.nome_campo, valor, posInicio, posFim });
          } else if (config.obrigatorio) {
            return { success: false, dados: [], erro: `Campo obrigatório "${config.nome_campo}" não encontrado` };
          }
        } catch {
          // Regex invalido, ignorar
        }
      }
    }

    if (dados.length === 0) {
      return { success: false, dados: [], erro: 'Etiqueta não reconhecida pelos modelos configurados' };
    }
    return { success: true, dados };
  };

  // Auto-fill palete number from navigation state
  useEffect(() => {
    const state = location.state as { numeroPalete?: number } | null;
    if (state?.numeroPalete && !paleteInfo && !loading) {
      const num = String(state.numeroPalete);
      setNumeroPalete(num);
      buscarPalete(parseInt(num));
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Step 1: Buscar Palete
  const buscarPalete = async (numPalete: number) => {
    if (!numPalete || numPalete <= 0) {
      setError('Informe um número de palete válido');
      return;
    }

    setError('');
    setLoading(true);

    try {
      let result: any = null;
      try {
        result = await buscarDadosPaleteDescarregamento(numPalete);
      } catch (apiErr: any) {
        if (!navigator.onLine) {
          result = await buscarDadosPaleteDescarregamentoOffline(numPalete);
        } else {
          // Verificar se palete foi inutilizado
          const errMsg = apiErr?.message || '';
          if (errMsg.includes('inutilizado') || errMsg.includes('Palete Nao Utilizado')) {
            setError(`Palete #${numPalete} foi inutilizado e nao pode ser utilizado. Este palete recebeu uma ocorrencia automatica.`);
            setLoading(false);
            return;
          }
          throw apiErr;
        }
      }

      if (!result || !result.success || !result.palete) {
        if (result?.ja_descarregado) {
          setError(`Palete #${numPalete} já foi descarregado anteriormente e não pode ser bipado novamente.`);
        } else {
          setError(`Palete #${numPalete} não encontrado em nenhuma operação ativa`);
        }
        setLoading(false);
        return;
      }

      // Verificar se o palete já foi completamente descarregado
      if (result.ja_descarregado) {
        setError(`Palete #${numPalete} já foi descarregado anteriormente e não pode ser bipado novamente.`);
        setLoading(false);
        return;
      }

      if (!result.cliente) {
        setError('Palete encontrado, mas sem cliente associado');
        setLoading(false);
        return;
      }

      setPaleteInfo({
        id: result.palete.id,
        numero_palete: result.palete.numero_palete,
        area_nome: result.palete.area_nome,
        area_codigo: result.palete.area_codigo,
        area_regiao: result.palete.area_regiao,
        operacao_id: result.palete.operacao_id,
        numero_operacao: result.palete.numero_operacao,
        doca: result.palete.doca,
        area_entrega_id: result.palete.area_entrega_id,
        tipo_palete: result.palete.tipo_palete,
        data_agendamento: result.palete.data_agendamento,
      });

      setCliente(result.cliente);
      setEtiquetaModelos(result.etiqueta_modelos || []);
      setEtiquetaConfigs(result.etiqueta_configs || []);
      setTotalVolumesPreCarreg(result.total_volumes || 0);

      const temModelos = result.etiqueta_modelos && result.etiqueta_modelos.length > 0;
      const temConfigs = result.etiqueta_configs && result.etiqueta_configs.length > 0;
      if (!temModelos && !temConfigs) {
        setError('Este cliente não possui configurações de etiqueta cadastradas.');
        setLoading(false);
        return;
      }

      // Reutilizar conferência de descarregamento existente ou criar nova
      let confId: number | null = (result as any).conferencia_descarregamento_id || null;
      if (!confId) {
        try {
          const confResult = await criarConferencia({
            tipo: 'descarregamento',
            cliente_id: result.cliente.id,
            observacoes: `Descarregamento - Palete #${numPalete} - ${result.cliente.razao_social}`
          });
          if (confResult.success) {
            confId = confResult.conferencia.id;
          }
        } catch (confErr: any) {
          if (!navigator.onLine) {
            confId = await gerarTempConferenciaId();
            await salvarConferenciaPendente({
              temp_id: confId,
              tipo: 'descarregamento',
              cliente_id: result.cliente.id,
              observacoes: `Descarregamento - Palete #${numPalete} - ${result.cliente.razao_social}`,
            });
          } else {
            throw confErr;
          }
        }
      }

      if (!confId) {
        setError('Não foi possível criar a conferência');
        setLoading(false);
        return;
      }

      setConferenciaId(confId);

      // Códigos já bipados no descarregamento (sessão anterior)
      const codigosJaBipados = new Set<string>(
        ((result as any).leituras_descarregamento || []).map((l: any) => l.codigo_lido)
      );

      // Load existing readings for this palete as history
      if (result.leituras && result.leituras.length > 0) {
        const existingItems: HistoricoItem[] = result.leituras.map((l: any) => {
          const jaBipado = codigosJaBipados.has(l.codigo_lido);
          let status: EtiquetaStatus;
          let mensagem: string;
          if (l.resultado === 'removido') {
            status = 'removida';
            mensagem = 'Removida do palete';
          } else if (l.resultado === 'duplicado') {
            status = 'duplicado';
            mensagem = 'Etiqueta duplicada (leitura anterior)';
          } else if (jaBipado) {
            status = 'sucesso';
            mensagem = 'Conferida no descarregamento';
          } else {
            status = 'pendente';
            mensagem = 'Aguardando conferência no descarregamento';
          }
          return {
            id: nextId.current++,
            codigo: l.codigo_lido,
            status,
            mensagem,
            timestamp: new Date(l.lido_em),
            leituraId: l.id,
            conferenciaIdOrigem: l.conferencia_id || confId!,
          };
        });
        setHistorico(existingItems);

        // Popular pedidosVolumes a partir das leituras históricas
        const modelos = result.etiqueta_modelos || [];
        const modeloComVolume = modelos.find((m: any) =>
          m.ativo && m.campo_numero_pedido && m.campo_volume_atual && m.campo_total_volumes
        );
        if (modeloComVolume) {
          const volumesPorPedido: Record<string, { total_volumes: number; volumes_bipados: Set<number>; volume_excedente: boolean; codigos_bipados: Map<number, string> }> = {};
          for (const l of result.leituras) {
            if (l.resultado !== 'ok') continue;
            const dados = l.dados_decodificados;
            if (!dados) continue;
            const numeroPedido = dados[modeloComVolume.campo_numero_pedido!] as string | undefined;
            const volumeAtualStr = dados[modeloComVolume.campo_volume_atual!] as string | undefined;
            const totalVolumesStr = dados[modeloComVolume.campo_total_volumes!] as string | undefined;
            if (!numeroPedido || !volumeAtualStr || !totalVolumesStr) continue;
            const volAtual = parseInt(volumeAtualStr);
            const volTotal = parseInt(totalVolumesStr);
            if (isNaN(volAtual) || isNaN(volTotal) || volTotal <= 0) continue;
            const pedidoKey = String(numeroPedido);
            if (!volumesPorPedido[pedidoKey]) {
              volumesPorPedido[pedidoKey] = { total_volumes: volTotal, volumes_bipados: new Set<number>(), volume_excedente: false, codigos_bipados: new Map<number, string>() };
            }
            volumesPorPedido[pedidoKey].volumes_bipados.add(volAtual);
            volumesPorPedido[pedidoKey].codigos_bipados.set(volAtual, l.codigo_lido);
            volumesPorPedido[pedidoKey].total_volumes = Math.max(volumesPorPedido[pedidoKey].total_volumes, volTotal);
            if (volAtual > volTotal) volumesPorPedido[pedidoKey].volume_excedente = true;
          }
          if (Object.keys(volumesPorPedido).length > 0) {
            setPedidosVolumes(volumesPorPedido);
          }
        }
      }

      // Carregar totais logísticos do palete
      if ((result as any).totais_logisticos) {
        setTotaisLogisticos({
          peso_total: (result as any).totais_logisticos.peso_total || 0,
          cubagem_total: (result as any).totais_logisticos.cubagem_total || 0,
          volumes_total: (result as any).totais_logisticos.volumes_total || 0,
        });
      }

      // Carregar pedidos com ponto de corte (sempre atualizar, mesmo quando vazio)
      setPedidosComCorte(result.pedidos_com_corte || []);

      setEtapa('scanning');
    } catch (err: any) {
      setError(err.message || 'Erro ao buscar palete');
    } finally {
      setLoading(false);
    }
  };

  const handleBuscarPalete = (e: React.FormEvent) => {
    e.preventDefault();
    buscarPalete(parseInt(numeroPalete));
  };

  // Auto-detect: buscar palete pela primeira etiqueta bipada
  // Retorna os dados detectados para uso imediato (setState é assíncrono)
  const handleAutoDetectPalete = async (codigo: string): Promise<{ conferenciaId: number; cliente: ClienteInfo; paleteInfo: PaleteInfo; etiquetaModelos: any[]; etiquetaConfigs: any[] } | null> => {
    try {
      const identResult = await identificarPaletePorEtiqueta(codigo);
      if (!identResult.success || identResult.encontrado === false) {
        // Verificar se o palete já foi descarregado
        if ((identResult as any).ja_descarregado) {
          setError(`Este palete já foi descarregado anteriormente e não pode ser bipado novamente.`);
        }
        return null;
      }

      // Verificar se o palete já foi completamente descarregado
      if ((identResult as any).ja_descarregado) {
        setError(`Palete #${(identResult as any).palete?.numero_palete || ''} já foi descarregado anteriormente e não pode ser bipado novamente.`);
        return null;
      }

      const palete = identResult.palete;
      const clienteData = identResult.cliente;
      if (!palete || !clienteData) return null;

      const paleteInfoData: PaleteInfo = {
        id: palete.id,
        numero_palete: palete.numero_palete,
        area_nome: palete.area_nome,
        area_codigo: palete.area_codigo,
        area_regiao: palete.area_regiao,
        operacao_id: palete.operacao_id,
        numero_operacao: palete.numero_operacao,
        doca: palete.doca,
        area_entrega_id: palete.area_entrega_id,
        tipo_palete: palete.tipo_palete,
        data_agendamento: palete.data_agendamento,
      };

      setPaleteInfo(paleteInfoData);
      setCliente(clienteData);
      setEtiquetaModelos(identResult.etiqueta_modelos || []);
      setEtiquetaConfigs(identResult.etiqueta_configs || []);
      setTotalVolumesPreCarreg(identResult.total_volumes || 0);

      const temModelos = identResult.etiqueta_modelos && identResult.etiqueta_modelos.length > 0;
      const temConfigs = identResult.etiqueta_configs && identResult.etiqueta_configs.length > 0;
      if (!temModelos && !temConfigs) {
        setError('Este cliente não possui configurações de etiqueta cadastradas. Configure as etiquetas antes de continuar.');
        return null;
      }

      if ((identResult as any).totais_logisticos) {
        setTotaisLogisticos({
          peso_total: (identResult as any).totais_logisticos.peso_total || 0,
          cubagem_total: (identResult as any).totais_logisticos.cubagem_total || 0,
          volumes_total: (identResult as any).totais_logisticos.volumes_total || 0,
        });
      }

      // Reutilizar conferência de descarregamento existente ou criar nova
      let confId: number | null = (identResult as any).conferencia_descarregamento_id || null;
      if (!confId) {
        try {
          const confResult = await criarConferencia({
            tipo: 'descarregamento',
            cliente_id: clienteData.id,
            observacoes: `Descarregamento - Palete #${palete.numero_palete} - ${clienteData.razao_social}`
          });
          if (confResult.success) {
            confId = confResult.conferencia.id;
          }
        } catch {
          // Falha ao criar conferencia
        }
      }

      if (confId) {
        setConferenciaId(confId);

        // Códigos já bipados no descarregamento (sessão anterior)
        const codigosJaBipados = new Set<string>(
          ((identResult as any).leituras_descarregamento || []).map((l: any) => l.codigo_lido)
        );

        // Load existing readings
        if (identResult.leituras && identResult.leituras.length > 0) {
          const existingItems: HistoricoItem[] = identResult.leituras.map((l: any) => {
            const jaBipado = codigosJaBipados.has(l.codigo_lido);
            let status: EtiquetaStatus;
            let mensagem: string;
            if (l.resultado === 'removido') {
              status = 'removida';
              mensagem = 'Removida do palete';
            } else if (l.resultado === 'duplicado') {
              status = 'duplicado';
              mensagem = 'Etiqueta duplicada (leitura anterior)';
            } else if (jaBipado) {
              status = 'sucesso';
              mensagem = 'Conferida no descarregamento';
            } else {
              status = 'pendente';
              mensagem = 'Aguardando conferência no descarregamento';
            }
            return {
              id: nextId.current++,
              codigo: l.codigo_lido,
              status,
              mensagem,
              timestamp: new Date(l.lido_em),
              leituraId: l.id,
              conferenciaIdOrigem: l.conferencia_id || confId!,
            };
          });
          setHistorico(existingItems);

          // Popular pedidosVolumes a partir das leituras históricas (auto-detect)
          const modelos = identResult.etiqueta_modelos || [];
          const modeloComVolume = modelos.find((m: any) =>
            m.ativo && m.campo_numero_pedido && m.campo_volume_atual && m.campo_total_volumes
          );
          if (modeloComVolume) {
            const volumesPorPedido: Record<string, { total_volumes: number; volumes_bipados: Set<number>; volume_excedente: boolean; codigos_bipados: Map<number, string> }> = {};
            for (const l of identResult.leituras) {
              if (l.resultado !== 'ok') continue;
              const dados = l.dados_decodificados;
              if (!dados) continue;
              const numeroPedido = dados[modeloComVolume.campo_numero_pedido!] as string | undefined;
              const volumeAtualStr = dados[modeloComVolume.campo_volume_atual!] as string | undefined;
              const totalVolumesStr = dados[modeloComVolume.campo_total_volumes!] as string | undefined;
              if (!numeroPedido || !volumeAtualStr || !totalVolumesStr) continue;
              const volAtual = parseInt(volumeAtualStr);
              const volTotal = parseInt(totalVolumesStr);
              if (isNaN(volAtual) || isNaN(volTotal) || volTotal <= 0) continue;
              const pedidoKey = String(numeroPedido);
              if (!volumesPorPedido[pedidoKey]) {
                volumesPorPedido[pedidoKey] = { total_volumes: volTotal, volumes_bipados: new Set<number>(), volume_excedente: false, codigos_bipados: new Map<number, string>() };
              }
              volumesPorPedido[pedidoKey].volumes_bipados.add(volAtual);
              volumesPorPedido[pedidoKey].codigos_bipados.set(volAtual, l.codigo_lido);
              volumesPorPedido[pedidoKey].total_volumes = Math.max(volumesPorPedido[pedidoKey].total_volumes, volTotal);
              if (volAtual > volTotal) volumesPorPedido[pedidoKey].volume_excedente = true;
            }
            if (Object.keys(volumesPorPedido).length > 0) {
              setPedidosVolumes(volumesPorPedido);
            }
          }
        }
      }

      // Carregar pedidos com ponto de corte (sempre atualizar, mesmo quando vazio)
      setPedidosComCorte(identResult.pedidos_com_corte || []);

      return confId ? { conferenciaId: confId, cliente: clienteData, paleteInfo: paleteInfoData, etiquetaModelos: identResult.etiqueta_modelos || [], etiquetaConfigs: identResult.etiqueta_configs || [] } : null;
    } catch {
      return null;
    }
  };

  // Step 2: Scan Etiqueta (continuous loop)
  const handleScanEtiqueta = async (e: React.FormEvent) => {
    e.preventDefault();
    const codigo = (etiquetaRef.current?.value || '').trim();
    if (!codigo) return;

    setScanLoading(true);
    setUltimoStatus(null);
    setError('');

    try {
      // Variáveis locais para uso imediato (setState é assíncrono)
      let activeConferenciaId = conferenciaId;
      let activeCliente = cliente;
      let activePaleteInfo = paleteInfo;
      let activeEtiquetaModelos: any[] | undefined;
      let activeEtiquetaConfigs: any[] | undefined;

      // Auto-detect mode: if no palete yet, try to identify from label
      if (isAutoDetect && !paleteInfo && !conferenciaId) {
        const detected = await handleAutoDetectPalete(codigo);
        if (!detected) {
          // Instrução 1: Registrar como "Conferida" com não conformidade "Sem pré-carregamento"
          addHistorico({
            codigo,
            status: 'conferida',
            mensagem: 'Etiqueta não encontrada no pré-carregamento. Palete não identificado automaticamente.',
            timestamp: new Date(),
          });
          setUltimoStatus({ status: 'conferida', mensagem: 'Etiqueta conferida - Sem pré-carregamento' });

          // Registrar não conformidade automaticamente
          try {
            await criarNaoConformidade({
              tipo: 'sem_pre_carregamento',
              severidade: 'aviso',
              codigo_lido: codigo,
              descricao: 'Etiqueta não encontrada no pré-carregamento. Palete não identificado automaticamente.',
              dados_adicionais: {
                origem: 'descarregamento',
                modo: 'auto_detect',
              },
            });
          } catch {
            // Erro ao registrar não conformidade - não crítico
          }

          if (etiquetaRef.current) etiquetaRef.current.value = '';
          etiquetaRef.current?.focus();
          setScanLoading(false);
          return;
        }
        // Usar dados retornados diretamente (não dependem do state assíncrono)
        activeConferenciaId = detected.conferenciaId;
        activeCliente = detected.cliente;
        activePaleteInfo = detected.paleteInfo;
        activeEtiquetaModelos = detected.etiquetaModelos;
        activeEtiquetaConfigs = detected.etiquetaConfigs;
      }

      if (!activeConferenciaId || !activeCliente || !activePaleteInfo) {
        // This should not happen but just in case
        if (etiquetaRef.current) etiquetaRef.current.value = '';
        setScanLoading(false);
        return;
      }

      // 1. Parse the label (usar configs locais do auto-detect se disponíveis, pois setState é assíncrono)
      const parseResult = parseEtiqueta(codigo, activeEtiquetaModelos, activeEtiquetaConfigs);
      if (!parseResult.success) {
        addHistorico({
          codigo,
          status: 'erro_parse',
          mensagem: parseResult.erro || 'Etiqueta não reconhecida',
          timestamp: new Date(),
        });
        setUltimoStatus({ status: 'erro_parse', mensagem: parseResult.erro || 'Etiqueta não reconhecida' });
        if (etiquetaRef.current) etiquetaRef.current.value = '';
        etiquetaRef.current?.focus();
        setScanLoading(false);
        return;
      }

      // 2. Instrução 6: Verificar se a etiqueta já foi conferida no descarregamento (duplicata local)
      const jaConferida = historico.find(h => h.codigo === codigo && (h.status === 'conferida' || h.status === 'sucesso'));
      if (jaConferida) {
        // Verificar se no pré-carregamento essa etiqueta já foi marcada como duplicata do cliente
        // Se sim, registrar automaticamente como duplicata reconhecida
        const volumeAtualDado = parseResult.dados.find(d =>
          d.campo.toLowerCase().includes('volume_atual') || d.campo.toLowerCase().includes('volume atual')
        );
        const totalVolumesDado = parseResult.dados.find(d =>
          d.campo.toLowerCase().includes('total_volumes') || d.campo.toLowerCase().includes('total de volumes') || d.campo.toLowerCase().includes('total volumes')
        );
        const numeroPedidoDado = parseResult.dados.find(d =>
          d.campo.toLowerCase().includes('pedido') || d.campo.toLowerCase().includes('numero_pedido')
        );

        // Abrir modal de duplicata para o conferente informar o motivo
        playDuplicateSound();
        setDuplicataOcorrenciaManual('');
        setDuplicataObservacao('');
        setDuplicataModal({
          show: true,
          codigo,
          volumeAtual: volumeAtualDado ? parseInt(volumeAtualDado.valor) : null,
          totalVolumes: totalVolumesDado ? parseInt(totalVolumesDado.valor) : null,
          numeroPedido: numeroPedidoDado?.valor || null,
          leituraId: jaConferida.leituraId,
        });
        if (etiquetaRef.current) etiquetaRef.current.value = '';
        setScanLoading(false);
        return;
      }

      // 3. Build decoded data
      const dadosDecodificados: Record<string, unknown> = {};
      parseResult.dados.forEach(d => {
        dadosDecodificados[d.campo] = d.valor;
      });
      dadosDecodificados['numero_palete'] = activePaleteInfo.numero_palete;
      dadosDecodificados['area_entrega'] = activePaleteInfo.area_nome;

      // 4. Register reading via API
      let leituraResult: LeituraResponse;
      try {
        leituraResult = await registrarLeitura(activeConferenciaId, {
          tipo_leitura: 'manual',
          codigo_lido: codigo,
          dados_decodificados: dadosDecodificados,
          quantidade: 1
        });
      } catch (leitErr: any) {
        if (!navigator.onLine) {
          await salvarLeituraPendente({
            conferencia_id: activeConferenciaId,
            tipo_leitura: 'manual',
            codigo_lido: codigo,
            quantidade: 1,
            lido_em: new Date().toISOString(),
            dados_decodificados: dadosDecodificados,
          });
          addHistorico({
            codigo,
            status: 'sucesso',
            mensagem: 'Salvo offline',
            timestamp: new Date(),
          });
          setUltimoStatus({ status: 'sucesso', mensagem: 'Etiqueta registrada (offline)' });
          if (etiquetaRef.current) etiquetaRef.current.value = '';
          etiquetaRef.current?.focus();
          setScanLoading(false);
          return;
        }
        const isPaleteInutilizado = leitErr.message && (
          leitErr.message.includes('inutilizado') ||
          leitErr.message.includes('Palete Nao Utilizado')
        );
        addHistorico({
          codigo,
          status: 'erro',
          mensagem: isPaleteInutilizado
            ? `Palete inutilizado - ${leitErr.message}`
            : (leitErr.message || 'Erro ao registrar'),
          timestamp: new Date(),
        });
        setUltimoStatus({
          status: 'erro',
          mensagem: isPaleteInutilizado
            ? `Palete inutilizado! ${leitErr.message}`
            : (leitErr.message || 'Erro ao registrar leitura')
        });
        if (etiquetaRef.current) etiquetaRef.current.value = '';
        etiquetaRef.current?.focus();
        setScanLoading(false);
        return;
      }

      // 5. Process API response

      // 5a. Etiqueta já existe em outro palete - abrir modal de transferência
      if (leituraResult.em_outro_palete && leituraResult.outro_palete) {
        playDuplicateSound();
        const palAnterior = leituraResult.outro_palete.numero_palete;
        setTransferenciaModal({
          show: true,
          codigo,
          numeroPaleteAnterior: palAnterior || null,
          conferenciaIdOrigem: leituraResult.outro_palete.conferencia_id || 0,
          leituraIdOrigem: leituraResult.outro_palete.leitura_id || 0,
          dadosDecodificados,
        });
        if (etiquetaRef.current) etiquetaRef.current.value = '';
        setScanLoading(false);
        return;
      }

      // 5b. Duplicata no mesmo palete
      if (leituraResult.duplicado) {
        const volumeAtualDado = parseResult.dados.find(d =>
          d.campo.toLowerCase().includes('volume_atual') || d.campo.toLowerCase().includes('volume atual')
        );
        const totalVolumesDado = parseResult.dados.find(d =>
          d.campo.toLowerCase().includes('total_volumes') || d.campo.toLowerCase().includes('total de volumes') || d.campo.toLowerCase().includes('total volumes')
        );
        const numeroPedidoDado = parseResult.dados.find(d =>
          d.campo.toLowerCase().includes('pedido') || d.campo.toLowerCase().includes('numero_pedido')
        );

        // Abrir modal de duplicata
        playDuplicateSound();
        setDuplicataOcorrenciaManual('');
        setDuplicataObservacao('');
        setDuplicataModal({
          show: true,
          codigo,
          volumeAtual: volumeAtualDado ? parseInt(volumeAtualDado.valor) : null,
          totalVolumes: totalVolumesDado ? parseInt(totalVolumesDado.valor) : null,
          numeroPedido: numeroPedidoDado?.valor || null,
          leituraId: leituraResult.leitura?.id,
        });
      } else {
        // Acumular totais logísticos da etiqueta bipada
        if (leituraResult.dados_logisticos) {
          const dl = leituraResult.dados_logisticos;
          setTotaisLogisticos(prev => ({
            peso_total: prev.peso_total + (dl.peso_total || 0),
            cubagem_total: prev.cubagem_total + (dl.cubagem || 0),
            volumes_total: prev.volumes_total + (dl.volumes || 0),
          }));

          // Rastrear volumes por pedido para detecção dinâmica de divergências
          if (dl.numero_pedido && dl.total_volumes_pedido) {
            const volumeAtual = dl.volume_atual || 0;
            const totalVolumes = dl.total_volumes_pedido;
            const isExcedente = volumeAtual > totalVolumes;

            setPedidosVolumes(prev => {
              const existing = prev[dl.numero_pedido!] || { total_volumes: totalVolumes, volumes_bipados: new Set<number>(), volume_excedente: false, codigos_bipados: new Map<number, string>() };
              const novosVolumes = new Set(existing.volumes_bipados);
              const novosCodigos = new Map(existing.codigos_bipados);
              if (volumeAtual > 0) {
                novosVolumes.add(volumeAtual);
                novosCodigos.set(volumeAtual, codigo);
              }
              return {
                ...prev,
                [dl.numero_pedido!]: {
                  total_volumes: Math.max(existing.total_volumes, totalVolumes),
                  volumes_bipados: novosVolumes,
                  volume_excedente: existing.volume_excedente || isExcedente,
                  codigos_bipados: novosCodigos,
                },
              };
            });
          }
        }

        // Verificar se o volume atual excede o total de volumes do pedido
        const dl = leituraResult.dados_logisticos;
        if (dl && dl.volume_atual && dl.total_volumes_pedido && dl.volume_atual > dl.total_volumes_pedido) {
          // Atualizar etiqueta pendente para conferida se existir
          setHistorico(prev => {
            const idx = prev.findIndex(h => h.codigo === codigo && h.status === 'pendente');
            if (idx >= 0) {
              const updated = [...prev];
              updated[idx] = { ...updated[idx], status: 'divergencia' as EtiquetaStatus, mensagem: `Volume ${dl.volume_atual} excede o total de ${dl.total_volumes_pedido} volumes do pedido ${dl.numero_pedido || ''}` };
              return updated;
            }
            return [{ id: nextId.current++, codigo, status: 'divergencia' as EtiquetaStatus, mensagem: `Volume ${dl.volume_atual} excede o total de ${dl.total_volumes_pedido} volumes do pedido ${dl.numero_pedido || ''}`, timestamp: new Date(), leituraId: leituraResult.leitura?.id, conferenciaIdOrigem: activeConferenciaId ?? undefined }, ...prev];
          });
          setUltimoStatus({ status: 'divergencia', mensagem: `Divergência: Volume ${dl.volume_atual} é maior que o total de ${dl.total_volumes_pedido} volumes!` });
        } else {
          // Atualizar etiqueta pendente para conferida se existir no histórico
          setHistorico(prev => {
            const idx = prev.findIndex(h => h.codigo === codigo && h.status === 'pendente');
            if (idx >= 0) {
              const updated = [...prev];
              updated[idx] = { ...updated[idx], status: 'conferida' as EtiquetaStatus, mensagem: 'Conferida no descarregamento', leituraId: leituraResult.leitura?.id, conferenciaIdOrigem: activeConferenciaId ?? undefined };
              return updated;
            }
            return [{ id: nextId.current++, codigo, status: 'conferida' as EtiquetaStatus, mensagem: 'Conferida no descarregamento', timestamp: new Date(), leituraId: leituraResult.leitura?.id, conferenciaIdOrigem: activeConferenciaId ?? undefined }, ...prev];
          });
          setUltimoStatus({ status: 'conferida', mensagem: 'Etiqueta conferida com sucesso!' });
        }
      }
    } catch (err: any) {
      addHistorico({
        codigo,
        status: 'erro',
        mensagem: err.message || 'Erro inesperado',
        timestamp: new Date(),
      });
      setUltimoStatus({ status: 'erro', mensagem: err.message || 'Erro inesperado' });
    } finally {
      if (etiquetaRef.current) etiquetaRef.current.value = '';
      setScanLoading(false);
      setTimeout(() => etiquetaRef.current?.focus(), 50);
    }
  };

  // Transferência entre paletes
  const handleTransferir = async () => {
    if (!conferenciaId || !transferenciaModal.show) return;
    setTransferenciaLoading(true);
    setError('');

    try {
      const result = await transferirLeituraEntrePaletes(conferenciaId, {
        leitura_id_origem: transferenciaModal.leituraIdOrigem,
        conferencia_id_origem: transferenciaModal.conferenciaIdOrigem,
        codigo_lido: transferenciaModal.codigo,
        dados_decodificados: transferenciaModal.dadosDecodificados,
        tipo_leitura: 'manual',
        quantidade: 1,
      });

      if (result.success) {
        // Acumular totais logísticos
        if (result.dados_logisticos) {
          const dl = result.dados_logisticos;
          setTotaisLogisticos(prev => ({
            peso_total: prev.peso_total + (dl.peso_total || 0),
            cubagem_total: prev.cubagem_total + (dl.cubagem || 0),
            volumes_total: prev.volumes_total + (dl.volumes || 0),
          }));

          if (dl.numero_pedido && dl.total_volumes_pedido) {
            const volumeAtual = dl.volume_atual || 0;
            const totalVolumes = dl.total_volumes_pedido;
            setPedidosVolumes(prev => {
              const existing = prev[dl.numero_pedido!] || { total_volumes: totalVolumes, volumes_bipados: new Set<number>(), volume_excedente: false, codigos_bipados: new Map<number, string>() };
              const novosVolumes = new Set(existing.volumes_bipados);
              const novosCodigos = new Map(existing.codigos_bipados);
              if (volumeAtual > 0) {
                novosVolumes.add(volumeAtual);
                novosCodigos.set(volumeAtual, transferenciaModal.codigo);
              }
              return {
                ...prev,
                [dl.numero_pedido!]: {
                  total_volumes: Math.max(existing.total_volumes, totalVolumes),
                  volumes_bipados: novosVolumes,
                  volume_excedente: existing.volume_excedente || volumeAtual > totalVolumes,
                  codigos_bipados: novosCodigos,
                },
              };
            });
          }
        }

        const palAnterior = transferenciaModal.numeroPaleteAnterior;
        addHistorico({
          codigo: transferenciaModal.codigo,
          status: 'sucesso',
          mensagem: `Transferida do Palete #${palAnterior || '?'} para este palete`,
          timestamp: new Date(),
          leituraId: result.leitura?.id,
          conferenciaIdOrigem: conferenciaId!,
        });
        setUltimoStatus({ status: 'sucesso', mensagem: `Etiqueta transferida do Palete #${palAnterior || '?'}` });
      }
    } catch (err: any) {
      addHistorico({
        codigo: transferenciaModal.codigo,
        status: 'erro',
        mensagem: err.message || 'Erro ao transferir etiqueta',
        timestamp: new Date(),
      });
      setUltimoStatus({ status: 'erro', mensagem: err.message || 'Erro ao transferir' });
    } finally {
      setTransferenciaLoading(false);
      setTransferenciaModal({ show: false, codigo: '', numeroPaleteAnterior: null, conferenciaIdOrigem: 0, leituraIdOrigem: 0, dadosDecodificados: {} });
      setTimeout(() => etiquetaRef.current?.focus(), 50);
    }
  };

  const handleManterNoPaleteAnterior = () => {
    const palAnterior = transferenciaModal.numeroPaleteAnterior;
    addHistorico({
      codigo: transferenciaModal.codigo,
      status: 'divergencia',
      mensagem: `Mantida no Palete #${palAnterior || '?'} (não transferida)`,
      timestamp: new Date(),
    });
    setUltimoStatus({ status: 'divergencia', mensagem: `Etiqueta mantida no Palete #${palAnterior || '?'}` });
    setTransferenciaModal({ show: false, codigo: '', numeroPaleteAnterior: null, conferenciaIdOrigem: 0, leituraIdOrigem: 0, dadosDecodificados: {} });
    setTimeout(() => etiquetaRef.current?.focus(), 50);
  };

  // Step 3: Analisar Divergências (compara pré-carregamento vs descarregamento)
  const handleAnalisarDivergencias = async () => {
    if (!conferenciaId) return;

    setDivergenciaLoading(true);
    setError('');

    try {
      const result = await analisarDivergenciasVolumes(conferenciaId);

      if (!result.success) {
        setError('Erro ao analisar divergências de volumes');
        setDivergenciaLoading(false);
        return;
      }

      const pedidosIncompletos = result.analise_volumes.filter(
        (p: AnaliseVolumePedido) => p.status === 'incompleto'
      );

      if (pedidosIncompletos.length === 0) {
        setError('');
        setAnaliseVolumes(result.analise_volumes);
        setModoOcorrenciaUnica(true);
        setEtapa('divergencias');
        setDivergenciaLoading(false);
        return;
      }

      // Initialize forms for each divergent order
      const forms: Record<string, DivergenciaFormData> = {};
      for (const pedido of pedidosIncompletos) {
        const allEtiquetaIndices = new Set(pedido.leituras.map((_: any, idx: number) => idx));
        const allFaltantes = new Set(pedido.volumes_faltantes);
        forms[pedido.numero_pedido] = {
          pedidoKey: pedido.numero_pedido,
          ocorrencia_tipo: tiposOcorrencia.length > 0 ? tiposOcorrencia[0].value : 'volume_faltante',
          justificativa: '',
          selected: true,
          selectedEtiquetas: allEtiquetaIndices,
          selectedFaltantes: allFaltantes,
        };
      }

      setAnaliseVolumes(result.analise_volumes);
      setDivergenciaForms(forms);
      setEtapa('divergencias');
    } catch (err: any) {
      setError(err.message || 'Erro ao analisar divergências');
    } finally {
      setDivergenciaLoading(false);
    }
  };

  // Update individual divergence form
  const updateDivergenciaForm = (pedidoKey: string, field: 'ocorrencia_tipo' | 'justificativa' | 'selected', value: string | boolean) => {
    setDivergenciaForms(prev => ({
      ...prev,
      [pedidoKey]: { ...prev[pedidoKey], [field]: value }
    }));
  };

  const toggleAllDivergencias = (selected: boolean) => {
    setDivergenciaForms(prev => {
      const updated = { ...prev };
      for (const key of Object.keys(updated)) {
        updated[key] = { ...updated[key], selected };
      }
      return updated;
    });
  };

  const toggleEtiqueta = (pedidoKey: string, idx: number) => {
    setDivergenciaForms(prev => {
      const form = prev[pedidoKey];
      if (!form) return prev;
      const newSet = new Set(form.selectedEtiquetas);
      if (newSet.has(idx)) newSet.delete(idx); else newSet.add(idx);
      return { ...prev, [pedidoKey]: { ...form, selectedEtiquetas: newSet } };
    });
  };

  const toggleFaltante = (pedidoKey: string, vol: number) => {
    setDivergenciaForms(prev => {
      const form = prev[pedidoKey];
      if (!form) return prev;
      const newSet = new Set(form.selectedFaltantes);
      if (newSet.has(vol)) newSet.delete(vol); else newSet.add(vol);
      return { ...prev, [pedidoKey]: { ...form, selectedFaltantes: newSet } };
    });
  };

  const toggleAllEtiquetas = (pedidoKey: string, selectAll: boolean, totalLeituras: number) => {
    setDivergenciaForms(prev => {
      const form = prev[pedidoKey];
      if (!form) return prev;
      const newSet = selectAll ? new Set(Array.from({ length: totalLeituras }, (_, i) => i)) : new Set<number>();
      return { ...prev, [pedidoKey]: { ...form, selectedEtiquetas: newSet } };
    });
  };

  const toggleAllFaltantes = (pedidoKey: string, selectAll: boolean, volumesFaltantes: number[]) => {
    setDivergenciaForms(prev => {
      const form = prev[pedidoKey];
      if (!form) return prev;
      const newSet = selectAll ? new Set(volumesFaltantes) : new Set<number>();
      return { ...prev, [pedidoKey]: { ...form, selectedFaltantes: newSet } };
    });
  };

  // Save divergence occurrences
  const handleSalvarDivergencias = async () => {
    if (!conferenciaId || !cliente) return;

    setDivergenciaSaving(true);
    setError('');

    try {
      const pedidosIncompletos = analiseVolumes.filter(p => p.status === 'incompleto');
      const selectedForms = Object.values(divergenciaForms).filter(f => f.selected);

      if (selectedForms.length === 0) {
        setError('Selecione ao menos uma divergência para registrar');
        setDivergenciaSaving(false);
        return;
      }

      for (const form of selectedForms) {
        if (form.selectedEtiquetas.size === 0 && form.selectedFaltantes.size === 0) {
          setError(`Pedido ${form.pedidoKey}: selecione ao menos uma etiqueta ou volume faltante`);
          setDivergenciaSaving(false);
          return;
        }
      }

      const ocorrencias: DivergenciaOcorrenciaCreateData[] = selectedForms.map(form => {
        const pedido = pedidosIncompletos.find(p => p.numero_pedido === form.pedidoKey);
        const etiquetas: DivergenciaOcorrenciaCreateData['etiquetas'] = [];

        if (pedido?.leituras) {
          pedido.leituras.forEach((l, idx) => {
            if (form.selectedEtiquetas.has(idx)) {
              etiquetas.push({
                codigo_lido: l.codigo_lido,
                volume_atual: l.volume_atual,
                total_volumes: l.total_volumes,
                lido_em: l.lido_em,
                status: 'conferido',
              });
            }
          });
        }

        if (pedido?.volumes_faltantes) {
          for (const volFaltante of pedido.volumes_faltantes) {
            if (form.selectedFaltantes.has(volFaltante)) {
              etiquetas.push({
                codigo_lido: `FALTANTE - Pedido ${form.pedidoKey} - Volume ${volFaltante} de ${pedido.total_volumes}`,
                volume_atual: volFaltante,
                total_volumes: pedido.total_volumes,
                status: 'faltante',
              });
            }
          }
        }

        return {
          conferencia_id: conferenciaId,
          cliente_id: cliente.id,
          numero_pedido: form.pedidoKey,
          ocorrencia_tipo: modoOcorrenciaUnica ? ocorrenciaUnicaTipo : form.ocorrencia_tipo,
          justificativa: modoOcorrenciaUnica ? ocorrenciaUnicaJustificativa : form.justificativa,
          total_volumes_esperado: pedido?.total_volumes || 0,
          total_volumes_conferido: pedido?.quantidade_conferida || 0,
          total_volumes_faltante: pedido?.quantidade_faltante || 0,
          volumes_conferidos: pedido?.volumes_conferidos || [],
          volumes_faltantes: pedido?.volumes_faltantes || [],
          etiquetas,
        };
      });

      const result = await criarDivergenciaOcorrenciaBatch(ocorrencias);

      if (result.success) {
        const pedidosDivergentes = selectedForms.map(f => f.pedidoKey);
        setPedidosDivergentesRemovidos(pedidosDivergentes);
        setDivergenciaSuccess(true);
      } else {
        setError('Erro ao salvar ocorrências de divergência');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar ocorrências');
    } finally {
      setDivergenciaSaving(false);
    }
  };

  // Tratar resposta do modal de duplicata (modo automático)
  const handleDuplicataResponseAuto = async (tipo: 'erro_conferente' | 'duplicata_cliente') => {
    if (!conferenciaId || !cliente) return;

    const { codigo, volumeAtual, totalVolumes, numeroPedido } = duplicataModal;

    setDuplicataLoading(true);
    try {
      const tipoEncontrado = todosOcorrenciaTipos.find(t => t.automatico && t.tipo_automatico === tipo);
      if (!tipoEncontrado) {
        utils.showNotification(
          `Tipo de ocorrência automática "${tipo === 'erro_conferente' ? 'Erro do Conferente' : 'Produto Duplicado'}" não está cadastrado. Cadastre-o na página de Ocorrências.`,
          { type: 'error' }
        );
        setDuplicataLoading(false);
        return;
      }
      const ocorrenciaTipo = tipoEncontrado.codigo;
      const justificativa = tipo === 'erro_conferente'
        ? `[Descarregamento] Erro do conferente: etiqueta ${codigo} bipada em duplicidade`
        : `[Descarregamento] Produto recebido duplicado com a mesma etiqueta: ${codigo}`;

      await criarDivergenciaOcorrenciaBatch([{
        conferencia_id: conferenciaId,
        cliente_id: cliente.id,
        numero_pedido: numeroPedido || '',
        ocorrencia_tipo: ocorrenciaTipo,
        justificativa,
        total_volumes_esperado: totalVolumes || 0,
        total_volumes_conferido: volumeAtual || 0,
        total_volumes_faltante: 0,
        volumes_conferidos: [],
        volumes_faltantes: [],
        etiquetas: [{
          codigo_lido: codigo,
          volume_atual: volumeAtual || 0,
          total_volumes: totalVolumes || 0,
          status: 'duplicado',
        }],
      }]);
    } catch {
      // Erro ao registrar ocorrência - não crítico
    } finally {
      setDuplicataLoading(false);
    }

    const mensagem = tipo === 'erro_conferente'
      ? 'Duplicada - Erro do conferente'
      : 'Duplicada - Produto recebido duplicado';

    // Atualizar entrada existente no histórico ao invés de adicionar nova (evita duplicação)
    setHistorico(prev => {
      const idx = prev.findIndex(h => h.codigo === codigo && (h.status === 'sucesso' || h.status === 'conferida'));
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], status: 'duplicado' as EtiquetaStatus, mensagem };
        return updated;
      }
      return [{ id: nextId.current++, codigo, status: 'duplicado' as EtiquetaStatus, mensagem, timestamp: new Date() }, ...prev];
    });
    setUltimoStatus({ status: 'duplicado', mensagem });

    setDuplicataModal({ show: false, codigo: '' });
    setDuplicataOcorrenciaManual('');
    setDuplicataObservacao('');
    setTimeout(() => etiquetaRef.current?.focus(), 50);
  };

  // Tratar resposta do modal de duplicata (modo manual)
  const handleDuplicataResponseManual = async () => {
    if (!conferenciaId || !cliente) return;
    if (!duplicataOcorrenciaManual) {
      utils.showNotification('Selecione um tipo de ocorrência', { type: 'error' });
      return;
    }

    const { codigo, volumeAtual, totalVolumes, numeroPedido } = duplicataModal;
    const tipoSelecionado = todosOcorrenciaTipos.find(t => t.codigo === duplicataOcorrenciaManual);
    const observacao = duplicataObservacao.trim() || `[Descarregamento] Etiqueta duplicada: ${codigo}`;

    setDuplicataLoading(true);
    try {
      await criarDivergenciaOcorrenciaBatch([{
        conferencia_id: conferenciaId,
        cliente_id: cliente.id,
        numero_pedido: numeroPedido || '',
        ocorrencia_tipo: duplicataOcorrenciaManual,
        justificativa: observacao,
        total_volumes_esperado: totalVolumes || 0,
        total_volumes_conferido: volumeAtual || 0,
        total_volumes_faltante: 0,
        volumes_conferidos: [],
        volumes_faltantes: [],
        etiquetas: [{
          codigo_lido: codigo,
          volume_atual: volumeAtual || 0,
          total_volumes: totalVolumes || 0,
          status: 'duplicado',
        }],
      }]);
    } catch {
      // Erro ao registrar ocorrência - não crítico
    } finally {
      setDuplicataLoading(false);
    }

    const mensagem = `Duplicada - ${tipoSelecionado?.nome || duplicataOcorrenciaManual}`;
    // Atualizar entrada existente no histórico ao invés de adicionar nova (evita duplicação)
    setHistorico(prev => {
      const idx = prev.findIndex(h => h.codigo === codigo && (h.status === 'sucesso' || h.status === 'conferida'));
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], status: 'duplicado' as EtiquetaStatus, mensagem };
        return updated;
      }
      return [{ id: nextId.current++, codigo, status: 'duplicado' as EtiquetaStatus, mensagem, timestamp: new Date() }, ...prev];
    });
    setUltimoStatus({ status: 'duplicado', mensagem });

    setDuplicataModal({ show: false, codigo: '' });
    setDuplicataOcorrenciaManual('');
    setDuplicataObservacao('');
    setTimeout(() => etiquetaRef.current?.focus(), 50);
  };

  // Reset
  const handleReset = () => {
    setNumeroPalete('');
    setPaleteInfo(null);
    setCliente(null);
    setEtiquetaModelos([]);
    setEtiquetaConfigs([]);
    if (etiquetaRef.current) etiquetaRef.current.value = '';
    setConferenciaId(null);
    setHistorico([]);
    setUltimoStatus(null);
    setError('');
    setAnaliseVolumes([]);
    setDivergenciaForms({});
    setDivergenciaSuccess(false);
    setPedidosDivergentesRemovidos([]);
    setModoOcorrenciaUnica(false);
    setOcorrenciaUnicaTipo(tiposOcorrencia.length > 0 ? tiposOcorrencia[0].value : 'volume_faltante');
    setOcorrenciaUnicaJustificativa('');
    setExpandedPedido(null);
    setTotaisLogisticos({ peso_total: 0, cubagem_total: 0, volumes_total: 0 });
    setPedidosVolumes({});
    setPedidosComCorte([]);
    setTotalVolumesPreCarreg(0);
    setTransferenciaModal({ show: false, codigo: '', numeroPaleteAnterior: null, conferenciaIdOrigem: 0, leituraIdOrigem: 0, dadosDecodificados: {} });
    if (isPWA) {
      navigate(backUrl);
      return;
    }
    if (isAutoDetect) {
      setEtapa('scanning');
    } else {
      setEtapa('palete');
    }
  };

  const pedidosIncompletos = analiseVolumes.filter(p => p.status === 'incompleto');
  const pedidosCompletos = analiseVolumes.filter(p => p.status === 'completo');

  return (
    <div className={isPWA ? 'pwa-page-wrapper' : ''}>
      {isPWA && (
        <div className="pwa-green-header">
          <button className="pwa-header-back" onClick={() => navigate(backUrl)} aria-label="Voltar ao dashboard">
            <i className="fas fa-arrow-left"></i>
          </button>
          <div className="pwa-header-info">
            <span className="pwa-header-title">Descarregamento</span>
            {contadores.total > 0 && (
              <span className="pwa-header-badge">{contadores.total} bipada{contadores.total !== 1 ? 's' : ''}</span>
            )}
          </div>
        </div>
      )}
      <div className={isPWA ? 'page-container pwa-content' : 'page-container'}>
      {!isPWA && (
        <button className="conferencia-back-link" onClick={() => navigate(backUrl)}>
          <i className="fas fa-arrow-left"></i> Voltar
        </button>
      )}

      <div className="precarreg-container">
        {!isPWA && (
        <div className="precarreg-header">
          <div className="precarreg-title">
            <i className="fas fa-arrow-down"></i>
            <h2>Descarregamento</h2>
          </div>
          {contadores.total > 0 && (
            <div className="precarreg-counter">
              <i className="fas fa-barcode"></i>
              <span>{contadores.total} bipada{contadores.total !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
        )}

        {/* Step indicator */}
        <div className="precarreg-steps">
          {!isAutoDetect && (
            <>
              <div className={`precarreg-step active ${etapa === 'scanning' || etapa === 'divergencias' ? 'completed' : ''}`}>
                <div className="step-circle">
                  {etapa === 'scanning' || etapa === 'divergencias' ? <i className="fas fa-check"></i> : 1}
                </div>
                <span className="step-label">Palete</span>
                <div className="step-line" />
              </div>
            </>
          )}
          <div className={`precarreg-step ${etapa === 'scanning' ? 'active' : ''} ${etapa === 'divergencias' ? 'completed' : ''}`}>
            <div className="step-circle">
              {etapa === 'divergencias' ? <i className="fas fa-check"></i> : (isAutoDetect ? 1 : 2)}
            </div>
            <span className="step-label">Bipagem</span>
            <div className="step-line" />
          </div>
          <div className={`precarreg-step ${etapa === 'divergencias' ? 'active' : ''}`}>
            <div className="step-circle">{isAutoDetect ? 2 : 3}</div>
            <span className="step-label">Divergências</span>
          </div>
        </div>

        {error && (
          <div className="precarreg-alert error">
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
          </div>
        )}

        {/* Step 1: Número do Palete (only in manual mode) */}
        {etapa === 'palete' && !isAutoDetect && (
          <div className="precarreg-card">
            <div className="precarreg-card-header">
              <i className="fas fa-pallet"></i>
              <h3>Número do Palete</h3>
            </div>
            <p className="precarreg-description">
              Informe o número do palete para iniciar o descarregamento. O sistema identificará o cliente automaticamente.
            </p>
            <form onSubmit={handleBuscarPalete}>
              <div className="precarreg-input-group">
                <label>Palete</label>
                <div className="precarreg-input-with-action">
                  <input
                    ref={paleteRef}
                    type="number"
                    inputMode={showKeyboard ? 'numeric' : 'none'}
                    className="precarreg-input"
                    value={numeroPalete}
                    onChange={(e) => setNumeroPalete(e.target.value)}
                    placeholder="Número do palete"
                    autoFocus
                    min="1"
                  />
                  <button
                    type="button"
                    className={`precarreg-keyboard-toggle ${showKeyboard ? 'active' : ''}`}
                    onClick={() => {
                      setShowKeyboard(!showKeyboard);
                      setTimeout(() => {
                        if (paleteRef.current) {
                          paleteRef.current.blur();
                          paleteRef.current.focus();
                        }
                      }, 100);
                    }}
                    title={showKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                  >
                    <i className="fas fa-keyboard"></i>
                  </button>
                </div>
              </div>
              <button type="submit" className="precarreg-btn primary" disabled={loading || !numeroPalete}>
                {loading ? <><i className="fas fa-spinner fa-spin"></i> Buscando...</> : <><i className="fas fa-search"></i> Buscar Palete</>}
              </button>
            </form>
          </div>
        )}

        {/* Step 2: Bipagem Contínua */}
        {(etapa === 'scanning') && (paleteInfo && cliente ? true : isAutoDetect) && (
          <>
            <div className="precarreg-card">
              {/* Client info */}
              {cliente && (
                <div className="precarreg-cliente-info">
                  <span className="cliente-nome">{cliente.razao_social}</span>
                  <span className="cliente-cnpj">CNPJ: {formatCnpj(cliente.cnpj)}</span>
                </div>
              )}

              {/* Auto-detect hint */}
              {isAutoDetect && !paleteInfo && (
                <div style={{
                  padding: '8px 14px', background: '#e8f5e9', borderRadius: 8,
                  borderLeft: '3px solid #27ae60', marginBottom: 12, fontSize: '0.85rem', color: '#2e7d32'
                }}>
                  <i className="fas fa-magic" style={{ marginRight: 8 }}></i>
                  Modo automático: bipe a primeira etiqueta para identificar o palete
                </div>
              )}

              {/* Palete & Area info */}
              {paleteInfo && (
                <div className="precarreg-info-badges">
                  <div className="precarreg-badge blue">
                    <i className="fas fa-pallet"></i>
                    <strong>Palete #{paleteInfo.numero_palete}</strong>
                  </div>
                  {exibirAreaDescarregamento && (
                    <div className="precarreg-badge green">
                      <i className="fas fa-map-marker-alt"></i>
                      {paleteInfo.area_codigo || paleteInfo.area_nome}
                      {paleteInfo.area_codigo && paleteInfo.area_nome && <span className="badge-sub"> ({paleteInfo.area_nome})</span>}
                    </div>
                  )}
                  {paleteInfo.tipo_palete === 'agendamento' && (
                    <div className="precarreg-badge" style={{ background: '#fde8d0', color: '#a04000' }}>
                      <i className="fas fa-calendar-alt"></i>
                      Agendamento{paleteInfo.data_agendamento ? ` - ${new Date(paleteInfo.data_agendamento).toLocaleString('pt-BR')}` : ''}
                    </div>
                  )}
                </div>
              )}

              {/* Comparação pré-carregamento vs descarregamento */}
              {totalVolumesPreCarreg > 0 && (
                <div style={{
                  padding: '10px 14px', background: diferencaPreCarreg?.status === 'ok' ? '#e8f5e9' : diferencaPreCarreg?.status === 'faltante' ? '#ffebee' : '#fff3e0',
                  borderRadius: 8,
                  borderLeft: `3px solid ${diferencaPreCarreg?.status === 'ok' ? '#27ae60' : diferencaPreCarreg?.status === 'faltante' ? '#e74c3c' : '#f39c12'}`,
                  marginBottom: 12, fontSize: '0.85rem',
                  color: diferencaPreCarreg?.status === 'ok' ? '#2e7d32' : diferencaPreCarreg?.status === 'faltante' ? '#c62828' : '#e65100'
                }}>
                  <i className={`fas ${diferencaPreCarreg?.status === 'ok' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`} style={{ marginRight: 8 }}></i>
                  <strong>Pré-carregamento: {totalVolumesPreCarreg} etiqueta(s)</strong>
                  {' | '}
                  <strong>Descarregamento: {contadores.sucesso} etiqueta(s)</strong>
                  {contadores.pendente > 0 && (
                    <span style={{ marginLeft: 4 }}>({contadores.pendente} pendente{contadores.pendente !== 1 ? 's' : ''})</span>
                  )}
                  {Object.keys(pedidosVolumes).length > 0 && (
                    <span style={{ marginLeft: 8 }}>| <strong>{Object.keys(pedidosVolumes).length} pedido(s)</strong></span>
                  )}
                  {diferencaPreCarreg && diferencaPreCarreg.status !== 'ok' && (
                    <span style={{ marginLeft: 8, fontWeight: 700 }}>
                      ({diferencaPreCarreg.diferenca} {diferencaPreCarreg.status === 'faltante' ? 'faltante(s) - divergência' : 'excedente(s) - divergência'})
                    </span>
                  )}
                  {diferencaPreCarreg?.status === 'ok' && (
                    <span style={{ marginLeft: 8, fontWeight: 700 }}>
                      (Quantidades conferem!)
                    </span>
                  )}
                </div>
              )}

              {/* Summary counters */}
              {(contadores.total > 0 || contadores.pendente > 0) && (
                <div className="precarreg-counters">
                  <div className="precarreg-counter-chip success">
                    <i className="fas fa-check-double"></i>
                    {contadores.sucesso} Conferida{contadores.sucesso !== 1 ? 's' : ''}
                  </div>
                  {contadores.pendente > 0 && (
                    <div className="precarreg-counter-chip" style={{ background: '#fff3e0', color: '#ff9800' }}>
                      <i className="fas fa-clock"></i>
                      {contadores.pendente} Pendente{contadores.pendente !== 1 ? 's' : ''}
                    </div>
                  )}
                  {contadores.duplicado > 0 && (
                    <div className="precarreg-counter-chip warning">
                      <i className="fas fa-clone"></i>
                      {contadores.duplicado} Duplicado{contadores.duplicado > 1 ? 's' : ''}
                    </div>
                  )}
                  {contadores.divergencia > 0 && (
                    <div className="precarreg-counter-chip danger">
                      <i className="fas fa-exclamation-triangle"></i>
                      {contadores.divergencia} Divergência{contadores.divergencia > 1 ? 's' : ''}
                    </div>
                  )}
                  {contadores.erro > 0 && (
                    <div className="precarreg-counter-chip purple-chip">
                      <i className="fas fa-times-circle"></i>
                      {contadores.erro} Erro{contadores.erro > 1 ? 's' : ''}
                    </div>
                  )}
                  {contadores.removida > 0 && (
                    <div className="precarreg-counter-chip" style={{ background: '#f3f4f6', color: '#6b7280' }}>
                      <i className="fas fa-trash-alt"></i>
                      {contadores.removida} Removida{contadores.removida > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              )}

              {/* Totais logísticos do palete */}
              {paleteInfo && (totaisLogisticos.peso_total > 0 || totaisLogisticos.cubagem_total > 0 || totaisLogisticos.volumes_total > 0) && (
                <div style={{
                  display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8,
                  padding: '10px 12px', background: '#f0f7ff', borderRadius: 8, border: '1px solid #d0e3ff'
                }}>
                  {totaisLogisticos.peso_total > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', color: '#1565c0' }}>
                      <i className="fas fa-weight-hanging" style={{ width: 16 }}></i>
                      <strong>{totaisLogisticos.peso_total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg</strong>
                    </div>
                  )}
                  {totaisLogisticos.cubagem_total > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', color: '#6a1b9a' }}>
                      <i className="fas fa-cube" style={{ width: 16 }}></i>
                      <strong>{totaisLogisticos.cubagem_total.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} m³</strong>
                    </div>
                  )}
                  {totaisLogisticos.volumes_total > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', color: '#2e7d32' }}>
                      <i className="fas fa-boxes-stacked" style={{ width: 16 }}></i>
                      <strong>{totaisLogisticos.volumes_total} vol.</strong>
                    </div>
                  )}
                </div>
              )}

              {/* Last scan status feedback */}
              {ultimoStatus && (
                <div
                  className="precarreg-scan-feedback"
                  style={{
                    background: STATUS_CONFIG[ultimoStatus.status].bg,
                    color: STATUS_CONFIG[ultimoStatus.status].color,
                    borderLeftColor: STATUS_CONFIG[ultimoStatus.status].color,
                  }}
                >
                  <i className={STATUS_CONFIG[ultimoStatus.status].icon}></i>
                  {ultimoStatus.mensagem}
                </div>
              )}

              {/* Scan input */}
              <form onSubmit={handleScanEtiqueta}>
                <div className="precarreg-input-group">
                  <label>Bipe a próxima etiqueta</label>
                  <div className="precarreg-input-with-action">
                    <input
                      ref={etiquetaRef}
                      type="text"
                      inputMode={showKeyboard ? 'text' : 'none'}
                      className="precarreg-input mono"
                      placeholder="Bipe ou digite o QR Code / Etiqueta"
                      autoFocus
                      disabled={scanLoading}
                    />
                    {habilitarCameraScanner ? (
                      <div className="precarreg-scan-options-wrapper" ref={scanOptionsRef}>
                        <button
                          type="button"
                          className={`precarreg-scan-options-btn ${showScanOptions ? 'open' : ''}`}
                          onClick={() => setShowScanOptions(!showScanOptions)}
                          title="Opções de entrada"
                        >
                          <i className={`fas ${showScanOptions ? 'fa-times' : 'fa-plus'}`}></i>
                        </button>
                        {showScanOptions && (
                          <div className="precarreg-scan-options-menu">
                            <button
                              type="button"
                              onClick={() => {
                                setShowKeyboard(!showKeyboard);
                                setShowScanOptions(false);
                                setTimeout(() => {
                                  if (etiquetaRef.current) {
                                    etiquetaRef.current.blur();
                                    etiquetaRef.current.focus();
                                  }
                                }, 100);
                              }}
                            >
                              <i className={`fas fa-keyboard scan-option-icon-keyboard ${showKeyboard ? 'active' : ''}`}></i>
                              {showKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowScanOptions(false);
                                setShowCameraScanner(true);
                              }}
                              disabled={scanLoading}
                            >
                              <i className="fas fa-camera scan-option-icon-camera"></i>
                              Escanear com câmera
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={`precarreg-keyboard-toggle ${showKeyboard ? 'active' : ''}`}
                        onClick={() => {
                          setShowKeyboard(!showKeyboard);
                          setTimeout(() => {
                            if (etiquetaRef.current) {
                              etiquetaRef.current.blur();
                              etiquetaRef.current.focus();
                            }
                          }, 100);
                        }}
                        title={showKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                      >
                        <i className="fas fa-keyboard"></i>
                      </button>
                    )}
                  </div>
                </div>
                <div className="precarreg-btn-group">
                  <button type="button" className="precarreg-btn secondary" onClick={handleReset}>
                    <i className="fas fa-redo"></i> {isPWA ? 'Voltar' : (isAutoDetect ? 'Voltar' : 'Novo Palete')}
                  </button>
                  <button type="submit" className="precarreg-btn primary" disabled={scanLoading}>
                    {scanLoading ? <><i className="fas fa-spinner fa-spin"></i> Registrando...</> : <><i className="fas fa-barcode"></i> Registrar</>}
                  </button>
                </div>
              </form>

              {/* Button to analyze divergences */}
              {contadores.sucesso > 0 && (temDivergenciaReal || (totalVolumesPreCarreg > 0 && diferencaPreCarreg?.status !== 'ok')) && (
                <div style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 16 }}>
                  <button
                    type="button"
                    className="precarreg-btn primary"
                    style={{ width: '100%', background: '#e74c3c' }}
                    onClick={handleAnalisarDivergencias}
                    disabled={divergenciaLoading}
                  >
                    {divergenciaLoading
                      ? <><i className="fas fa-spinner fa-spin"></i> Analisando...</>
                      : <><i className="fas fa-search-minus"></i> Analisar Divergências</>
                    }
                  </button>
                </div>
              )}
            </div>

            {/* Histórico de Bipagem */}
            {historico.length > 0 && (
              <div className="precarreg-card" style={{ marginTop: 16 }}>
                <div className="precarreg-card-header" style={{ cursor: 'pointer' }} onClick={() => setHistoricoVisivel(v => !v)}>
                  <i className="fas fa-history"></i>
                  <h3>Histórico de Bipagem ({historico.length})</h3>
                  <button
                    type="button"
                    className="precarreg-toggle-historico-btn"
                    title={historicoVisivel ? 'Ocultar histórico' : 'Mostrar histórico'}
                  >
                    <i className={historicoVisivel ? 'fas fa-chevron-up' : 'fas fa-chevron-down'}></i>
                    {historicoVisivel ? 'Ocultar' : 'Mostrar'}
                  </button>
                </div>
                {historicoVisivel && <div className="precarreg-data-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Etiqueta</th>
                        <th>Status</th>
                        <th>Hora</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historico.map((item) => {
                        const badge = STATUS_CONFIG[item.status];
                        return (
                          <tr key={item.id} style={item.status === 'removida' ? { opacity: 0.5 } : undefined}>
                            <td className="precarreg-hist-etiqueta">
                              <span className="hist-codigo" style={item.status === 'removida' ? { textDecoration: 'line-through' } : undefined}>{item.codigo}</span>
                              {item.mensagem && (
                                <span className="hist-detalhe">{item.mensagem}</span>
                              )}
                            </td>
                            <td>
                              <span
                                className="precarreg-status-badge"
                                style={{ background: badge.bg, color: badge.color }}
                              >
                                <i className={badge.icon}></i>
                                {badge.label}
                              </span>
                            </td>
                            <td className="precarreg-hist-hora">
                              {item.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>}
              </div>
            )}

            {/* Etiquetas por Pedido */}
            {Object.keys(pedidosVolumes).length > 0 && (
              <div className="precarreg-card" style={{ marginTop: 16 }}>
                <div className="precarreg-card-header" style={{ background: '#fffde7' }}>
                  <i className="fas fa-tags" style={{ color: '#f57f17' }}></i>
                  <h3 style={{ color: '#f57f17' }}>Etiquetas por Pedido</h3>
                </div>
                <div style={{ padding: '12px 14px' }}>
                  {Object.entries(pedidosVolumes).map(([pedido, info]) => {
                    const isCorte = pedidosComCorte.includes(pedido);
                    // Determinar quais volumes foram bipados no descarregamento
                    const codigosBipadosDescarreg = new Set(
                      historico
                        .filter(h => h.status === 'sucesso' || h.status === 'conferida')
                        .map(h => h.codigo)
                    );
                    // Para pedidos com corte: o total esperado é apenas o que foi bipado no pré-carregamento
                    const totalEsperado = isCorte ? info.volumes_bipados.size : info.total_volumes;
                    const faltantes: number[] = [];
                    let bipadosDescarreg = 0;
                    for (let v = 1; v <= info.total_volumes; v++) {
                      if (!info.volumes_bipados.has(v)) {
                        if (!isCorte) faltantes.push(v);
                      } else {
                        const codigoVol = info.codigos_bipados.get(v);
                        if (codigoVol && codigosBipadosDescarreg.has(codigoVol)) {
                          bipadosDescarreg++;
                        }
                      }
                    }
                    // Pegar o código bipado mais longo como referência para gerar os irmãos,
                    // pois códigos com zeros à esquerda (ex: "0002") são mais longos que sem (ex: "1")
                    const codigos = Array.from(info.codigos_bipados.values());
                    const codigoRef = codigos.length > 0
                      ? codigos.reduce((longest, current) => current.length > longest.length ? current : longest)
                      : null;

                    return (
                      <div key={pedido} style={{
                        marginBottom: 10, padding: '10px 12px', background: isCorte ? '#fff8e1' : '#fff', borderRadius: 8,
                        border: bipadosDescarreg === totalEsperado ? '1px solid #c8e6c9' : isCorte ? '1px solid #ffcc02' : '1px solid #ffe082'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 4 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#333' }}>
                              <i className="fas fa-file-invoice" style={{ marginRight: 4, color: '#1565c0' }}></i>
                              Pedido {pedido}
                            </span>
                            {isCorte && (
                              <span style={{
                                padding: '1px 6px', background: '#ff8f00', color: 'white',
                                borderRadius: 4, fontSize: '0.65rem', fontWeight: 600
                              }}>
                                CORTE
                              </span>
                            )}
                          </div>
                          <span style={{
                            fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: 12,
                            background: bipadosDescarreg === totalEsperado ? '#e8f5e9' : '#fff3e0',
                            color: bipadosDescarreg === totalEsperado ? '#2e7d32' : '#e65100'
                          }}>
                            {bipadosDescarreg}/{totalEsperado}{isCorte ? ` (de ${info.total_volumes})` : ''}
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {Array.from({ length: info.total_volumes }, (_, i) => i + 1)
                          .filter(v => !isCorte || info.volumes_bipados.has(v))
                          .map(v => {
                            const bipado = info.volumes_bipados.has(v);
                            const codigoVol = info.codigos_bipados.get(v);
                            const bipadoDescarreg = bipado && codigoVol && codigosBipadosDescarreg.has(codigoVol);
                            const codigoLabel = codigoVol
                              || (codigoRef ? gerarCodigoIrmao(codigoRef, v) : null)
                              || `Vol. ${v}/${info.total_volumes}`;

                            let bgColor: string, borderColor: string, iconClass: string, iconColor: string, textColor: string, statusLabel: string;
                            if (bipadoDescarreg) {
                              bgColor = '#e8f5e9'; borderColor = '#a5d6a7'; iconClass = 'fas fa-check-circle';
                              iconColor = '#2e7d32'; textColor = '#2e7d32'; statusLabel = 'Conferida';
                            } else if (bipado) {
                              bgColor = '#e3f2fd'; borderColor = '#90caf9'; iconClass = 'fas fa-clock';
                              iconColor = '#1565c0'; textColor = '#1565c0'; statusLabel = 'Aguardando';
                            } else {
                              bgColor = '#fff8e1'; borderColor = '#ffe082'; iconClass = 'fas fa-minus-circle';
                              iconColor = '#f57f17'; textColor = '#333'; statusLabel = 'Pendente';
                            }

                            return (
                              <div key={v} style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '6px 10px', borderRadius: 6, fontSize: '0.8rem',
                                background: bgColor, border: `1px solid ${borderColor}`,
                              }}>
                                <i className={iconClass} style={{ fontSize: '0.85rem', color: iconColor, flexShrink: 0 }}></i>
                                <span style={{
                                  fontFamily: 'monospace', fontWeight: 500, color: textColor,
                                  wordBreak: 'break-all', flex: 1
                                }}>
                                  {codigoLabel}
                                </span>
                                <span style={{
                                  fontSize: '0.7rem', fontWeight: 600, padding: '1px 6px', borderRadius: 8,
                                  background: bipadoDescarreg ? '#c8e6c9' : bipado ? '#bbdefb' : '#ffe082',
                                  color: bipadoDescarreg ? '#1b5e20' : bipado ? '#0d47a1' : '#e65100',
                                  whiteSpace: 'nowrap', flexShrink: 0
                                }}>
                                  {statusLabel}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* Step 3: Tratativa de Divergências */}
        {etapa === 'divergencias' && (
          <>
            {/* Success message */}
            {divergenciaSuccess ? (
              <div className="precarreg-card">
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <i className="fas fa-check-circle" style={{ fontSize: 48, color: '#27ae60', marginBottom: 16 }}></i>
                  <h3 style={{ color: '#27ae60', marginBottom: 8 }}>Ocorrências registradas com sucesso!</h3>
                  <p style={{ color: '#7f8c8d', marginBottom: 12 }}>
                    As divergências entre o pré-carregamento e o descarregamento foram registradas para análise.
                  </p>
                </div>

                {pedidosDivergentesRemovidos.length > 0 && (
                  <div style={{
                    margin: '0 20px 20px', padding: 20, background: '#fff3e0', borderRadius: 12,
                    border: '2px solid #ffb74d'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                      <div style={{
                        width: 48, height: 48, borderRadius: '50%', background: '#ff9800',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                      }}>
                        <i className="fas fa-hand-paper" style={{ fontSize: '1.3rem', color: 'white' }}></i>
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 8px', color: '#e65100', fontSize: '1rem' }}>
                          <i className="fas fa-exclamation-circle" style={{ marginRight: 6 }}></i>
                          Divergência detectada
                        </h4>
                        <p style={{ margin: '0 0 12px', fontSize: '0.9rem', color: '#bf360c', lineHeight: 1.5 }}>
                          A quantidade de etiquetas bipadas no pré-carregamento ({totalVolumesPreCarreg}) difere do descarregamento ({contadores.sucesso}).
                          {' '}Os pedidos abaixo foram marcados como divergentes para investigação.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {pedidosDivergentesRemovidos.map(pedido => (
                            <div key={pedido} style={{
                              padding: '8px 14px', background: '#fff8e1', borderRadius: 8,
                              border: '1px solid #ffe082', display: 'flex', alignItems: 'center', gap: 8,
                              fontSize: '0.9rem'
                            }}>
                              <i className="fas fa-tag" style={{ color: '#f57c00' }}></i>
                              <strong>Pedido {pedido}</strong>
                              <span style={{ color: '#8d6e63', fontSize: '0.8rem' }}>- divergência a ser investigada</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="precarreg-btn-group" style={{ justifyContent: 'center', padding: '0 20px 20px' }}>
                  <button type="button" className="precarreg-btn secondary" onClick={() => setEtapa('scanning')}>
                    <i className="fas fa-arrow-left"></i> Voltar
                  </button>
                  {!isPWA && (
                    <button type="button" className="precarreg-btn primary" onClick={() => navigate('/nao-conformidades')}>
                      <i className="fas fa-exclamation-triangle"></i> Ver Não Conformidades
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="precarreg-card">
                  <div className="precarreg-card-header">
                    <i className="fas fa-chart-bar"></i>
                    <h3>Resumo da Análise - Descarregamento</h3>
                  </div>

                  {/* Comparação pré-carregamento vs descarregamento */}
                  {totalVolumesPreCarreg > 0 && (
                    <div style={{
                      padding: '12px 16px', marginTop: 12, borderRadius: 8,
                      background: diferencaPreCarreg?.status === 'ok' ? '#e8f5e9' : '#ffebee',
                      border: `1px solid ${diferencaPreCarreg?.status === 'ok' ? '#a5d6a7' : '#ef9a9a'}`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <i className={`fas ${diferencaPreCarreg?.status === 'ok' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}
                          style={{ color: diferencaPreCarreg?.status === 'ok' ? '#27ae60' : '#e74c3c', fontSize: '1.2rem' }}></i>
                        <strong style={{ color: diferencaPreCarreg?.status === 'ok' ? '#2e7d32' : '#c62828' }}>
                          {diferencaPreCarreg?.status === 'ok'
                            ? 'Quantidades conferem!'
                            : 'Divergência entre pré-carregamento e descarregamento!'}
                        </strong>
                      </div>
                      <div style={{ display: 'flex', gap: 16, fontSize: '0.9rem' }}>
                        <span>Pré-carregamento: <strong>{totalVolumesPreCarreg}</strong> etiqueta(s)</span>
                        <span>Descarregamento: <strong>{contadores.sucesso}</strong> etiqueta(s)</span>
                        {diferencaPreCarreg && diferencaPreCarreg.status !== 'ok' && (
                          <span style={{ color: '#c62828', fontWeight: 700 }}>
                            {diferencaPreCarreg.diferenca} {diferencaPreCarreg.status === 'faltante' ? 'faltante(s)' : 'excedente(s)'}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="precarreg-counters" style={{ marginTop: 12 }}>
                    <div className="precarreg-counter-chip success">
                      <i className="fas fa-check-circle"></i>
                      {pedidosCompletos.length} Pedido{pedidosCompletos.length !== 1 ? 's' : ''} completo{pedidosCompletos.length !== 1 ? 's' : ''}
                    </div>
                    {pedidosIncompletos.length > 0 && (
                      <div className="precarreg-counter-chip danger">
                        <i className="fas fa-exclamation-triangle"></i>
                        {pedidosIncompletos.length} Pedido{pedidosIncompletos.length !== 1 ? 's' : ''} com divergência{pedidosIncompletos.length !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>

                  {pedidosIncompletos.length === 0 && (
                    <>
                      {diferencaPreCarreg && diferencaPreCarreg.status !== 'ok' ? (
                        <div style={{ padding: '20px' }}>
                          <div style={{
                            padding: '16px', background: '#fff3e0', borderRadius: 8,
                            border: '1px solid #ffb74d', marginBottom: 16
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                              <i className="fas fa-exclamation-triangle" style={{ color: '#f39c12', fontSize: '1.2rem' }}></i>
                              <strong style={{ color: '#e65100' }}>Divergência geral detectada</strong>
                            </div>
                            <p style={{ margin: 0, fontSize: '0.9rem', color: '#bf360c' }}>
                              Todos os pedidos estão completos individualmente, porém existe uma divergência geral
                              entre o pré-carregamento ({totalVolumesPreCarreg} etiquetas) e o descarregamento ({contadores.sucesso} etiquetas).
                              Por favor, registre a ocorrência abaixo.
                            </p>
                          </div>
                          <div style={{ padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
                            <div className="precarreg-input-group" style={{ marginBottom: 12 }}>
                              <label>Tipo de Ocorrência</label>
                              <select
                                className="precarreg-input"
                                value={ocorrenciaUnicaTipo}
                                onChange={e => setOcorrenciaUnicaTipo(e.target.value)}
                              >
                                {tiposOcorrencia.map(t => (
                                  <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="precarreg-input-group" style={{ marginBottom: 12 }}>
                              <label>Justificativa</label>
                              <textarea
                                className="precarreg-input"
                                value={ocorrenciaUnicaJustificativa}
                                onChange={e => setOcorrenciaUnicaJustificativa(e.target.value)}
                                placeholder="Descreva a justificativa para a divergência..."
                                rows={3}
                                style={{ resize: 'vertical', fontFamily: 'inherit' }}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                              <button
                                type="button"
                                className={`precarreg-btn ${modoOcorrenciaUnica ? 'primary' : 'secondary'}`}
                                onClick={() => setModoOcorrenciaUnica(true)}
                                style={{ flex: 1 }}
                              >
                                <i className="fas fa-layer-group"></i> Ocorrência geral
                              </button>
                              <button
                                type="button"
                                className={`precarreg-btn ${!modoOcorrenciaUnica ? 'primary' : 'secondary'}`}
                                onClick={() => {
                                  setModoOcorrenciaUnica(false);
                                  // Initialize forms for all orders if not yet initialized
                                  if (analiseVolumes.length > 0 && Object.keys(divergenciaForms).length === 0) {
                                    const forms: Record<string, DivergenciaFormData> = {};
                                    for (const pedido of analiseVolumes) {
                                      forms[pedido.numero_pedido] = {
                                        pedidoKey: pedido.numero_pedido,
                                        ocorrencia_tipo: tiposOcorrencia.length > 0 ? tiposOcorrencia[0].value : 'volume_faltante',
                                        justificativa: '',
                                        selected: false,
                                        selectedEtiquetas: new Set(pedido.leituras.map((_: any, idx: number) => idx)),
                                        selectedFaltantes: new Set(pedido.volumes_faltantes),
                                      };
                                    }
                                    setDivergenciaForms(forms);
                                  }
                                }}
                                style={{ flex: 1 }}
                              >
                                <i className="fas fa-list"></i> Individual por pedido
                              </button>
                            </div>
                          </div>

                          {modoOcorrenciaUnica ? (
                            <div style={{ padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
                              <div className="precarreg-input-group" style={{ marginBottom: 12 }}>
                                <label>Tipo de Ocorrência</label>
                                <select
                                  className="precarreg-input"
                                  value={ocorrenciaUnicaTipo}
                                  onChange={e => setOcorrenciaUnicaTipo(e.target.value)}
                                >
                                  {tiposOcorrencia.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="precarreg-input-group" style={{ marginBottom: 12 }}>
                                <label>Justificativa</label>
                                <textarea
                                  className="precarreg-input"
                                  value={ocorrenciaUnicaJustificativa}
                                  onChange={e => setOcorrenciaUnicaJustificativa(e.target.value)}
                                  placeholder="Descreva a justificativa para a divergência..."
                                  rows={3}
                                  style={{ resize: 'vertical', fontFamily: 'inherit' }}
                                />
                              </div>
                              <div className="precarreg-btn-group">
                                <button type="button" className="precarreg-btn secondary" onClick={() => setEtapa('scanning')}>
                                  <i className="fas fa-arrow-left"></i> Voltar
                                </button>
                                <button
                                  type="button"
                                  className="precarreg-btn primary"
                                  style={{ background: '#e74c3c' }}
                                  onClick={async () => {
                                    if (!conferenciaId || !cliente) return;
                                    setDivergenciaSaving(true);
                                    try {
                                      await criarDivergenciaOcorrenciaBatch([{
                                        conferencia_id: conferenciaId,
                                        cliente_id: cliente.id,
                                        numero_pedido: '',
                                        ocorrencia_tipo: ocorrenciaUnicaTipo,
                                        justificativa: ocorrenciaUnicaJustificativa || `Divergência geral: Pré-carregamento ${totalVolumesPreCarreg} vs Descarregamento ${contadores.sucesso}`,
                                        total_volumes_esperado: totalVolumesPreCarreg,
                                        total_volumes_conferido: contadores.sucesso,
                                        total_volumes_faltante: Math.abs(totalVolumesPreCarreg - contadores.sucesso),
                                        volumes_conferidos: [],
                                        volumes_faltantes: [],
                                        etiquetas: [],
                                      }]);
                                      setDivergenciaSuccess(true);
                                    } catch (err: any) {
                                      setError(err.message || 'Erro ao salvar ocorrência');
                                    } finally {
                                      setDivergenciaSaving(false);
                                    }
                                  }}
                                  disabled={divergenciaSaving}
                                >
                                  {divergenciaSaving
                                    ? <><i className="fas fa-spinner fa-spin"></i> Salvando...</>
                                    : <><i className="fas fa-save"></i> Registrar Ocorrência</>
                                  }
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              {/* Individual per-order occurrence forms */}
                              <div style={{ marginTop: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                  <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#2c3e50' }}>
                                    <i className="fas fa-clipboard-list" style={{ marginRight: 6 }}></i>
                                    Selecione os pedidos para registrar ocorrência
                                  </h4>
                                  <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                    <input
                                      type="checkbox"
                                      checked={Object.values(divergenciaForms).length > 0 && Object.values(divergenciaForms).every(f => f.selected)}
                                      onChange={e => toggleAllDivergencias(e.target.checked)}
                                    />
                                    Selecionar todos
                                  </label>
                                </div>
                                {analiseVolumes.map(pedido => {
                                  const form = divergenciaForms[pedido.numero_pedido];
                                  if (!form) return null;
                                  const isExpanded = expandedPedido === pedido.numero_pedido;

                                  return (
                                    <div
                                      key={pedido.numero_pedido}
                                      style={{
                                        border: form.selected ? '2px solid #e74c3c' : '1px solid #e0e0e0',
                                        borderRadius: 8, marginBottom: 12, overflow: 'hidden',
                                        background: form.selected ? '#fff5f5' : '#fff',
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: 'flex', alignItems: 'center', gap: 12,
                                          padding: '12px 16px', cursor: 'pointer',
                                          background: form.selected ? '#ffebee' : '#f8f9fa',
                                        }}
                                        onClick={() => setExpandedPedido(isExpanded ? null : pedido.numero_pedido)}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={form.selected}
                                          onChange={e => {
                                            e.stopPropagation();
                                            updateDivergenciaForm(pedido.numero_pedido, 'selected', e.target.checked);
                                          }}
                                          onClick={e => e.stopPropagation()}
                                        />
                                        <div style={{ flex: 1 }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                            <strong style={{ fontSize: '1rem' }}>Pedido {pedido.numero_pedido}</strong>
                                            {pedido.tem_ponto_corte && (
                                              <span style={{
                                                padding: '1px 6px', background: '#ff8f00', color: 'white',
                                                borderRadius: 4, fontSize: '0.65rem', fontWeight: 600
                                              }}>
                                                CORTE
                                              </span>
                                            )}
                                            <span
                                              className="precarreg-status-badge"
                                              style={{
                                                background: pedido.status === 'completo' ? '#e8f5e9' : '#ffebee',
                                                color: pedido.status === 'completo' ? '#27ae60' : '#e74c3c',
                                                fontSize: '0.75rem'
                                              }}
                                            >
                                              <i className={`fas ${pedido.status === 'completo' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                                              {pedido.status === 'completo' ? 'Completo' : `${pedido.quantidade_faltante} faltante(s)`}
                                            </span>
                                          </div>
                                          <div style={{ fontSize: '0.85rem', color: '#7f8c8d', marginTop: 4 }}>
                                            Conferidos: {pedido.quantidade_conferida} de {pedido.total_volumes}
                                            {pedido.tem_ponto_corte && (
                                              <span style={{ color: '#e65100', marginLeft: 6 }}>
                                                (ponto de corte - volumes parciais neste palete)
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ color: '#7f8c8d' }}></i>
                                      </div>

                                      {isExpanded && form.selected && (
                                        <div style={{ padding: 16, background: '#f8f9fa' }}>
                                          <div className="precarreg-input-group" style={{ marginBottom: 12 }}>
                                            <label>Tipo de Ocorrência</label>
                                            <select
                                              className="precarreg-input"
                                              value={form.ocorrencia_tipo}
                                              onChange={e => updateDivergenciaForm(pedido.numero_pedido, 'ocorrencia_tipo', e.target.value)}
                                            >
                                              {tiposOcorrencia.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                              ))}
                                            </select>
                                          </div>
                                          <div className="precarreg-input-group">
                                            <label>Justificativa</label>
                                            <textarea
                                              className="precarreg-input"
                                              value={form.justificativa}
                                              onChange={e => updateDivergenciaForm(pedido.numero_pedido, 'justificativa', e.target.value)}
                                              placeholder="Descreva a justificativa para esta divergência..."
                                              rows={3}
                                              style={{ resize: 'vertical', fontFamily: 'inherit' }}
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>

                              <div className="precarreg-btn-group" style={{ marginTop: 16 }}>
                                <button type="button" className="precarreg-btn secondary" onClick={() => setEtapa('scanning')}>
                                  <i className="fas fa-arrow-left"></i> Voltar
                                </button>
                                <button
                                  type="button"
                                  className="precarreg-btn primary"
                                  style={{ background: '#e74c3c' }}
                                  onClick={async () => {
                                    if (!conferenciaId || !cliente) return;
                                    const selectedForms = Object.values(divergenciaForms).filter(f => f.selected);
                                    if (selectedForms.length === 0) return;
                                    setDivergenciaSaving(true);
                                    try {
                                      const batch: DivergenciaOcorrenciaCreateData[] = selectedForms.map(form => {
                                        const pedido = analiseVolumes.find(p => p.numero_pedido === form.pedidoKey);
                                        return {
                                          conferencia_id: conferenciaId,
                                          cliente_id: cliente.id,
                                          numero_pedido: form.pedidoKey,
                                          ocorrencia_tipo: form.ocorrencia_tipo,
                                          justificativa: form.justificativa || `Divergência no pedido ${form.pedidoKey}`,
                                          total_volumes_esperado: pedido?.total_volumes || 0,
                                          total_volumes_conferido: pedido?.quantidade_conferida || 0,
                                          total_volumes_faltante: pedido?.quantidade_faltante || 0,
                                          volumes_conferidos: pedido?.volumes_conferidos || [],
                                          volumes_faltantes: pedido?.volumes_faltantes || [],
                                          etiquetas: (pedido?.leituras || [])
                                            .filter((_: any, idx: number) => form.selectedEtiquetas.has(idx))
                                            .map((l: any) => l.codigo_lido),
                                        };
                                      });
                                      await criarDivergenciaOcorrenciaBatch(batch);
                                      setDivergenciaSuccess(true);
                                    } catch (err: any) {
                                      setError(err.message || 'Erro ao salvar ocorrências');
                                    } finally {
                                      setDivergenciaSaving(false);
                                    }
                                  }}
                                  disabled={divergenciaSaving || Object.values(divergenciaForms).every(f => !f.selected)}
                                >
                                  {divergenciaSaving
                                    ? <><i className="fas fa-spinner fa-spin"></i> Salvando...</>
                                    : <><i className="fas fa-save"></i> Registrar Ocorrências ({Object.values(divergenciaForms).filter(f => f.selected).length})</>
                                  }
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '30px 20px' }}>
                          <i className="fas fa-check-circle" style={{ fontSize: 40, color: '#27ae60', marginBottom: 12 }}></i>
                          <h3 style={{ color: '#27ae60' }}>Nenhuma divergência encontrada!</h3>
                          <p style={{ color: '#7f8c8d' }}>Todos os pedidos estão completos. Nenhuma divergência encontrada.</p>
                          <button type="button" className="precarreg-btn secondary" onClick={() => setEtapa('scanning')} style={{ marginTop: 16 }}>
                            <i className="fas fa-arrow-left"></i> Voltar
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Divergent orders */}
                {pedidosIncompletos.length > 0 && (
                  <>
                    {/* Mode selector */}
                    <div className="precarreg-card" style={{ marginTop: 16 }}>
                      <div className="precarreg-card-header">
                        <i className="fas fa-cog"></i>
                        <h3>Modo de Registro</h3>
                      </div>
                      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                        <button
                          type="button"
                          className={`precarreg-btn ${!modoOcorrenciaUnica ? 'primary' : 'secondary'}`}
                          onClick={() => setModoOcorrenciaUnica(false)}
                          style={{ flex: 1 }}
                        >
                          <i className="fas fa-list"></i> Individual por pedido
                        </button>
                        <button
                          type="button"
                          className={`precarreg-btn ${modoOcorrenciaUnica ? 'primary' : 'secondary'}`}
                          onClick={() => { setModoOcorrenciaUnica(true); toggleAllDivergencias(true); }}
                          style={{ flex: 1 }}
                        >
                          <i className="fas fa-layer-group"></i> Ocorrência única
                        </button>
                      </div>

                      {modoOcorrenciaUnica && (
                        <div style={{ marginTop: 16, padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
                          <div className="precarreg-input-group" style={{ marginBottom: 12 }}>
                            <label>Tipo de Ocorrência</label>
                            <select
                              className="precarreg-input"
                              value={ocorrenciaUnicaTipo}
                              onChange={e => setOcorrenciaUnicaTipo(e.target.value)}
                            >
                              {tiposOcorrencia.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="precarreg-input-group">
                            <label>Justificativa</label>
                            <textarea
                              className="precarreg-input"
                              value={ocorrenciaUnicaJustificativa}
                              onChange={e => setOcorrenciaUnicaJustificativa(e.target.value)}
                              placeholder="Descreva a justificativa para todas as divergências selecionadas..."
                              rows={3}
                              style={{ resize: 'vertical', fontFamily: 'inherit' }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Pedidos divergentes list */}
                    <div className="precarreg-card" style={{ marginTop: 16 }}>
                      <div className="precarreg-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <i className="fas fa-exclamation-triangle" style={{ color: '#e74c3c' }}></i>
                          <h3>Pedidos com Divergência ({pedidosIncompletos.length})</h3>
                        </div>
                        {!modoOcorrenciaUnica && (
                          <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={Object.values(divergenciaForms).every(f => f.selected)}
                              onChange={e => toggleAllDivergencias(e.target.checked)}
                            />
                            Selecionar todos
                          </label>
                        )}
                      </div>

                      <div style={{ marginTop: 12 }}>
                        {pedidosIncompletos.map(pedido => {
                          const form = divergenciaForms[pedido.numero_pedido];
                          if (!form) return null;
                          const isExpanded = expandedPedido === pedido.numero_pedido;

                          return (
                            <div
                              key={pedido.numero_pedido}
                              style={{
                                border: form.selected ? '2px solid #e74c3c' : '1px solid #e0e0e0',
                                borderRadius: 8, marginBottom: 12, overflow: 'hidden',
                                background: form.selected ? '#fff5f5' : '#fff',
                              }}
                            >
                              {/* Order header */}
                              <div
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 12,
                                  padding: '12px 16px', cursor: 'pointer',
                                  background: form.selected ? '#ffebee' : '#f8f9fa',
                                }}
                                onClick={() => setExpandedPedido(isExpanded ? null : pedido.numero_pedido)}
                              >
                                {!modoOcorrenciaUnica && (
                                  <input
                                    type="checkbox"
                                    checked={form.selected}
                                    onChange={e => {
                                      e.stopPropagation();
                                      updateDivergenciaForm(pedido.numero_pedido, 'selected', e.target.checked);
                                    }}
                                    onClick={e => e.stopPropagation()}
                                  />
                                )}
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <strong style={{ fontSize: '1rem' }}>Pedido {pedido.numero_pedido}</strong>
                                    {pedido.tem_ponto_corte && (
                                      <span style={{
                                        padding: '1px 6px', background: '#ff8f00', color: 'white',
                                        borderRadius: 4, fontSize: '0.65rem', fontWeight: 600
                                      }}>
                                        CORTE
                                      </span>
                                    )}
                                    <span
                                      className="precarreg-status-badge"
                                      style={{ background: '#ffebee', color: '#e74c3c', fontSize: '0.75rem' }}
                                    >
                                      <i className="fas fa-exclamation-triangle"></i>
                                      {pedido.quantidade_faltante} volume{pedido.quantidade_faltante !== 1 ? 's' : ''} faltante{pedido.quantidade_faltante !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: '0.85rem', color: '#7f8c8d', marginTop: 4 }}>
                                    Conferidos: {pedido.quantidade_conferida} de {pedido.total_volumes} |
                                    Volumes bipados: [{pedido.volumes_conferidos.join(', ')}] |
                                    Faltantes: [{pedido.volumes_faltantes.join(', ')}]
                                  </div>
                                </div>
                                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ color: '#7f8c8d' }}></i>
                              </div>

                              {/* Expanded details */}
                              {isExpanded && (
                                <div style={{ padding: 16 }}>
                                  {/* Labels scanned for this order */}
                                  <div style={{ marginBottom: 16 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                      <h4 style={{ fontSize: '0.9rem', color: '#2c3e50', margin: 0 }}>
                                        <i className="fas fa-barcode" style={{ marginRight: 6 }}></i>
                                        Etiquetas bipadas ({pedido.leituras.length})
                                      </h4>
                                      {form.selected && (
                                        <label style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: '#7f8c8d' }}>
                                          <input
                                            type="checkbox"
                                            checked={form.selectedEtiquetas.size === pedido.leituras.length}
                                            onChange={e => toggleAllEtiquetas(pedido.numero_pedido, e.target.checked, pedido.leituras.length)}
                                          />
                                          Selecionar todas
                                        </label>
                                      )}
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                      {pedido.leituras.map((l, idx) => {
                                        const isSelected = form.selectedEtiquetas.has(idx);
                                        return (
                                          <div
                                            key={idx}
                                            style={{
                                              display: 'flex', alignItems: 'center', gap: 8,
                                              padding: '6px 10px',
                                              background: isSelected ? '#e8f5e9' : '#f5f5f5',
                                              borderRadius: 4, fontSize: '0.85rem',
                                              border: isSelected ? '1px solid #c8e6c9' : '1px solid #e0e0e0',
                                              cursor: form.selected ? 'pointer' : 'default',
                                              opacity: isSelected ? 1 : 0.6,
                                            }}
                                            onClick={() => form.selected && toggleEtiqueta(pedido.numero_pedido, idx)}
                                          >
                                            {form.selected && (
                                              <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => toggleEtiqueta(pedido.numero_pedido, idx)}
                                                onClick={e => e.stopPropagation()}
                                              />
                                            )}
                                            <code style={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>{l.codigo_lido}</code>
                                            {l.resultado && l.resultado !== 'ok' && (
                                              <span style={{
                                                padding: '1px 6px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 600,
                                                color: l.resultado === 'duplicado' ? '#d97706' : l.resultado === 'removido' ? '#6b7280' : '#dc2626',
                                                background: l.resultado === 'duplicado' ? '#fffbeb' : l.resultado === 'removido' ? '#f3f4f6' : '#fef2f2',
                                                whiteSpace: 'nowrap'
                                              }}>
                                                {l.resultado === 'duplicado' ? 'Duplicada' : l.resultado === 'removido' ? 'Removida' : 'Divergente'}
                                              </span>
                                            )}
                                            <span style={{ color: '#27ae60', whiteSpace: 'nowrap', marginLeft: 8 }}>
                                              Vol. {l.volume_atual}/{l.total_volumes}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>

                                  {/* Missing volumes */}
                                  <div style={{ marginBottom: 16 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                      <h4 style={{ fontSize: '0.9rem', color: '#e74c3c', margin: 0 }}>
                                        <i className="fas fa-times-circle" style={{ marginRight: 6 }}></i>
                                        Volumes faltantes ({pedido.volumes_faltantes.length})
                                      </h4>
                                      {form.selected && (
                                        <label style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: '#7f8c8d' }}>
                                          <input
                                            type="checkbox"
                                            checked={form.selectedFaltantes.size === pedido.volumes_faltantes.length}
                                            onChange={e => toggleAllFaltantes(pedido.numero_pedido, e.target.checked, pedido.volumes_faltantes)}
                                          />
                                          Selecionar todos
                                        </label>
                                      )}
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                      {pedido.volumes_faltantes.map(vol => {
                                        const isSelected = form.selectedFaltantes.has(vol);
                                        return (
                                          <span
                                            key={vol}
                                            onClick={() => form.selected && toggleFaltante(pedido.numero_pedido, vol)}
                                            style={{
                                              display: 'inline-flex', alignItems: 'center', gap: 6,
                                              padding: '4px 12px',
                                              background: isSelected ? '#ffebee' : '#f5f5f5',
                                              color: isSelected ? '#e74c3c' : '#999',
                                              borderRadius: 20, fontSize: '0.85rem', fontWeight: 600,
                                              cursor: form.selected ? 'pointer' : 'default',
                                              border: isSelected ? '1px solid #ffcdd2' : '1px solid #e0e0e0',
                                              opacity: isSelected ? 1 : 0.6,
                                            }}
                                          >
                                            {form.selected && (
                                              <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => toggleFaltante(pedido.numero_pedido, vol)}
                                                onClick={e => e.stopPropagation()}
                                                style={{ margin: 0 }}
                                              />
                                            )}
                                            Volume {vol} de {pedido.total_volumes}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  </div>

                                  {/* Individual occurrence form */}
                                  {!modoOcorrenciaUnica && form.selected && (
                                    <div style={{ padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
                                      <h4 style={{ fontSize: '0.9rem', color: '#2c3e50', marginBottom: 12 }}>
                                        <i className="fas fa-file-alt" style={{ marginRight: 6 }}></i>
                                        Ocorrência para este pedido
                                      </h4>
                                      <div className="precarreg-input-group" style={{ marginBottom: 12 }}>
                                        <label>Tipo de Ocorrência</label>
                                        <select
                                          className="precarreg-input"
                                          value={form.ocorrencia_tipo}
                                          onChange={e => updateDivergenciaForm(pedido.numero_pedido, 'ocorrencia_tipo', e.target.value)}
                                        >
                                          {tiposOcorrencia.map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="precarreg-input-group">
                                        <label>Justificativa</label>
                                        <textarea
                                          className="precarreg-input"
                                          value={form.justificativa}
                                          onChange={e => updateDivergenciaForm(pedido.numero_pedido, 'justificativa', e.target.value)}
                                          placeholder="Descreva a justificativa para esta divergência..."
                                          rows={3}
                                          style={{ resize: 'vertical', fontFamily: 'inherit' }}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="precarreg-card" style={{ marginTop: 16 }}>
                      <div className="precarreg-btn-group">
                        <button
                          type="button"
                          className="precarreg-btn secondary"
                          onClick={() => setEtapa('scanning')}
                        >
                          <i className="fas fa-arrow-left"></i> Voltar à Bipagem
                        </button>
                        <button
                          type="button"
                          className="precarreg-btn primary"
                          style={{ background: '#e74c3c' }}
                          onClick={handleSalvarDivergencias}
                          disabled={divergenciaSaving || Object.values(divergenciaForms).every(f => !f.selected)}
                        >
                          {divergenciaSaving
                            ? <><i className="fas fa-spinner fa-spin"></i> Salvando...</>
                            : <><i className="fas fa-save"></i> Registrar Ocorrências ({Object.values(divergenciaForms).filter(f => f.selected).length})</>
                          }
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>

    {/* Modal de etiqueta duplicada */}
    {duplicataModal.show && (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', zIndex: 2000, padding: 20,
      }}>
        <div style={{
          background: '#fff', borderRadius: 16, padding: 24,
          width: '100%', maxWidth: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: '#fff3e0',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12,
            }}>
              <i className="fas fa-clone" style={{ fontSize: 24, color: '#f39c12' }}></i>
            </div>
            <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '1.1rem' }}>Etiqueta Duplicada</h3>
          </div>

          {/* Resumo */}
          <div style={{
            background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8,
            padding: 12, marginBottom: 20, fontSize: '0.9rem', color: '#5d4037',
          }}>
            <p style={{ margin: 0, marginBottom: 8 }}>
              <strong>Etiqueta:</strong> <code style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>{duplicataModal.codigo}</code>
            </p>
            {duplicataModal.volumeAtual && duplicataModal.totalVolumes && (
              <p style={{ margin: 0, marginBottom: 8 }}>
                <strong>Volume {duplicataModal.volumeAtual} de {duplicataModal.totalVolumes}</strong> já foi bipado anteriormente.
              </p>
            )}
            {duplicataModal.numeroPedido && (
              <p style={{ margin: 0, marginBottom: 8 }}>
                <strong>Pedido:</strong> {duplicataModal.numeroPedido}
              </p>
            )}
            <p style={{ margin: 0, fontWeight: 600 }}>
              Por favor, revise e selecione o motivo da duplicidade:
            </p>
          </div>

          {/* Opções de ocorrência */}
          {temAutomaticoDuplicata ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <button
                  type="button"
                  className="precarreg-btn secondary"
                  style={{
                    width: '100%', padding: '14px 16px', textAlign: 'left',
                    display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.95rem',
                  }}
                  onClick={() => handleDuplicataResponseAuto('erro_conferente')}
                  disabled={duplicataLoading}
                >
                  <i className="fas fa-user-times" style={{ fontSize: 18, color: '#3498db', minWidth: 24 }}></i>
                  <div>
                    <strong>Erro do Conferente</strong>
                    <div style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: 2 }}>
                      O mesmo item foi bipado duas vezes por engano
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  className="precarreg-btn secondary"
                  style={{
                    width: '100%', padding: '14px 16px', textAlign: 'left',
                    display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.95rem',
                    border: '2px solid #e74c3c', background: '#fff5f5',
                  }}
                  onClick={() => handleDuplicataResponseAuto('duplicata_cliente')}
                  disabled={duplicataLoading}
                >
                  <i className="fas fa-box" style={{ fontSize: 18, color: '#e74c3c', minWidth: 24 }}></i>
                  <div>
                    <strong style={{ color: '#c0392b' }}>Produto Recebido Duplicado</strong>
                    <div style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: 2 }}>
                      O produto foi recebido duplicado com a mesma etiqueta
                    </div>
                  </div>
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '0.9rem', color: '#2c3e50' }}>
                    Tipo de Ocorrência *
                  </label>
                  <select
                    value={duplicataOcorrenciaManual}
                    onChange={e => setDuplicataOcorrenciaManual(e.target.value)}
                    disabled={duplicataLoading}
                    style={{
                      width: '100%', padding: '10px 12px', border: '1px solid #ddd',
                      borderRadius: 8, fontSize: '0.95rem', background: '#fff',
                      color: duplicataOcorrenciaManual ? '#2c3e50' : '#999',
                    }}
                  >
                    <option value="">Selecione o tipo de ocorrência...</option>
                    {todosOcorrenciaTipos.map(t => (
                      <option key={t.id} value={t.codigo}>{t.nome}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '0.9rem', color: '#2c3e50' }}>
                    Observação
                  </label>
                  <textarea
                    value={duplicataObservacao}
                    onChange={e => setDuplicataObservacao(e.target.value)}
                    disabled={duplicataLoading}
                    placeholder="Descreva o motivo da duplicidade..."
                    rows={3}
                    style={{
                      width: '100%', padding: '10px 12px', border: '1px solid #ddd',
                      borderRadius: 8, fontSize: '0.95rem', resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>

                <button
                  type="button"
                  className="precarreg-btn primary"
                  style={{
                    width: '100%', padding: '12px 16px', fontSize: '0.95rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                  onClick={handleDuplicataResponseManual}
                  disabled={duplicataLoading || !duplicataOcorrenciaManual}
                >
                  <i className="fas fa-save" style={{ fontSize: 16 }}></i>
                  Registrar Ocorrência
                </button>
              </div>
            </>
          )}

          {duplicataLoading && (
            <div style={{ textAlign: 'center', marginTop: 12, color: '#7f8c8d' }}>
              <i className="fas fa-spinner fa-spin"></i> Registrando ocorrência...
            </div>
          )}
        </div>
      </div>
    )}

    {/* Modal de transferência entre paletes */}
    {transferenciaModal.show && (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000, padding: 16
      }}>
        <div style={{
          background: 'white', borderRadius: 16, padding: '24px 20px', width: '100%', maxWidth: 440,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: '#fff3e0',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px'
            }}>
              <i className="fas fa-exchange-alt" style={{ fontSize: '1.5rem', color: '#f39c12' }}></i>
            </div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#2c3e50' }}>Etiqueta de Outro Palete</h3>
            <p style={{ margin: '8px 0 0', fontSize: '0.85rem', color: '#7f8c8d' }}>
              Esta etiqueta foi bipada no <strong>Palete #{transferenciaModal.numeroPaleteAnterior || '?'}</strong>.
            </p>
          </div>

          <div style={{
            padding: '10px 14px', background: '#f8f9fa', borderRadius: 8,
            fontFamily: 'monospace', fontSize: '0.85rem', color: '#2c3e50',
            wordBreak: 'break-all', marginBottom: 16, textAlign: 'center',
            border: '1px solid #e0e0e0'
          }}>
            {transferenciaModal.codigo}
          </div>

          <div style={{
            padding: '12px 14px', background: '#fff3e0', borderRadius: 8,
            fontSize: '0.85rem', color: '#e65100', marginBottom: 20, lineHeight: 1.5,
            border: '1px solid #ffe0b2'
          }}>
            <i className="fas fa-info-circle" style={{ marginRight: 6 }}></i>
            Durante o transporte, esta etiqueta pode ter sido misturada entre paletes.
            Deseja transferi-la para o palete atual?
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              onClick={handleTransferir}
              disabled={transferenciaLoading}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: 10, border: 'none',
                background: '#27ae60', color: 'white', fontWeight: 600,
                fontSize: '0.9rem', cursor: transferenciaLoading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
              }}
            >
              {transferenciaLoading
                ? <><i className="fas fa-spinner fa-spin"></i> Transferindo...</>
                : <><i className="fas fa-exchange-alt"></i> Transferir para este palete</>
              }
            </button>
            <button
              onClick={handleManterNoPaleteAnterior}
              disabled={transferenciaLoading}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: 10,
                border: '2px solid #e0e0e0', background: 'white',
                color: '#546e7a', fontWeight: 600, fontSize: '0.9rem',
                cursor: transferenciaLoading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
              }}
            >
              <i className="fas fa-ban"></i> Manter no Palete #{transferenciaModal.numeroPaleteAnterior || '?'}
            </button>
          </div>
        </div>
      </div>
    )}
    <CameraScannerModal
      show={showCameraScanner}
      onClose={() => setShowCameraScanner(false)}
      onScan={(code) => {
        setShowCameraScanner(false);
        if (etiquetaRef.current) etiquetaRef.current.value = code;
        // Auto-submit após leitura
        setTimeout(() => {
          if (etiquetaRef.current) {
            etiquetaRef.current.form?.requestSubmit();
          }
        }, 100);
      }}
    />
    </div>
  );
};

export default DescarregamentoConferencia;
